import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  DataForSEOService,
  SupportingPagesV2EvalData,
  SupportingPagesV2RowResult,
  SupportingPagesByTopicSnapshotData,
  SectionCoverageRow,
  HeadingPathSegment,
  PageDemandSnapshot,
  PageHealthSnapshot,
  PageRealizationSnapshot,
  PageIntentDiagnosis,
  PageIntentDiagnosisBullet,
  PageSerpResultRow,
  PageSerpSnapshot,
  SecondaryPageHealthSnapshot
} from '../../services/dataforseo';
import { FeatureFlagService } from '../../services/feature-flag';
import { formatPageDemandSourceConfidence } from '../../utils/page-demand-display';
import {
  classifyPageHealth,
  effectiveHealthBandClass,
  effectiveHealthBandLabel,
  EffectiveHealthDisplayBand,
  pageHealthBandClass,
  pageHealthBandLabel,
  resolveEffectiveHealthBand
} from '../content-inventory/inventory-page-health';
import {
  formatInventoryRealizationNumber,
  formatInventoryRealizationRatio,
  inventoryRealizationGapTitle,
  inventoryRealizationRatioTitle
} from '../content-inventory/inventory-realization-sort';
import {
  buildPageSignalsForUrl,
  type InventoryPageSignal,
  type InventoryPageSignalKind
} from '../content-inventory/inventory-page-signals';
import {
  InventoryPlanClustersService,
  type ActionPlanCannibalizationRow,
  type ActionPlanInternalLinkRow,
  type ActionPlanUnclusteredPageRow,
  type ActionPlanV2Row
} from '../../services/inventory-plan-clusters.service';
import { PlanV2IssueQueueService } from '../../services/plan-v2-issue-queue.service';
import { PlanV2EmbedContextService } from '../../services/plan-v2-embed-context.service';
import {
  comparePageFreshnessToSerp,
  formatFreshnessDateLabel,
  freshnessDiagnosisLabel as resolveFreshnessDiagnosisLabel,
  medianPublishedDate,
  pageContentLastModifiedLabel,
  parsePageContentLastModifiedDate,
  serpWinnerPublishedDatesFromResults
} from '../../utils/freshness-serp-compare';
import {
  buildFreshnessWorkItemContext,
  composeFreshnessWorkItem,
  composeInternalLinkWorkItem,
  composeInternalLinkWorkItemsFromSelection,
  buildInternalLinkRowDisplay,
  composeInternalLinksWorkItem,
  composeSerpIntentWorkItem,
  composeSerpPhraseWorkItem,
  composeTechnicalIssuesWorkItem,
  formatInternalLinkRelevanceScore,
  internalLinkDirectionChipClass,
  internalLinkDirectionLabel,
  filterInternalLinksIncomingToPage,
  filterInternalLinksPendingIssuesTriage,
  internalLinkSuggestionKeysInWorkItems,
  listInternalLinkComposeOptions,
  splitPlacementByAnchor,
  listTechnicalIssueComposeOptions,
  buildTopicOwnershipComposeState,
  composeTopicOwnershipWorkItemFromState,
  topicOwnershipComposeHasSelection,
  topicOwnershipHeadingComposeId,
  type InternalLinkComposeOption,
  type TechnicalIssueComposeOption,
  type TopicOwnershipComposeState
} from '../../utils/plan-v2-work-item-compose';
import {
  buildPlanV2IssueKey,
  type PlanV2IssueCardPayload,
  type PlanV2IssueType
} from '../../utils/plan-v2-issues';
import { DomainContextService } from '../../services/domain-context';
import { CompareContextService } from '../../services/compare-context';
import { UrlSerpRefreshProgress, UrlSerpSyncService } from '../../services/url-serp-sync';
import {
  buildSerpFoldedTermSegments,
  buildSerpSnippetSegments,
  type SerpSnippetSegment
} from '../../utils/serp-snippet-segments';
import {
  buildYourPageVocabularySegments,
  collectMisalignedPhraseLinks,
  foldLexicalToken,
  type MisalignedPhraseLink
} from '../../utils/serp-intent-highlight';
import { DomainTopicsService, ParentTopic } from '../../services/domain-topics';
import { InventoryQuickCompareDrawerComponent } from '../inventory-quick-compare-drawer/inventory-quick-compare-drawer';
import { PlanV2IssueCardActionsComponent } from '../plan-v2-issue-card-actions/plan-v2-issue-card-actions';
import {
  buildPageTopicLabelsBySlug,
  extractTopicTaggedPagesFromDiagnostic,
  filterSupportingPagesByRelevance,
  FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT,
  filterRowsByFocalAnchorTopics,
  formatSupportingPagePath,
  formatSupportingPageTraffic,
  getFocalAnchorTopicSlugs,
  getTopTopicsForSupportingPages,
  isComparisonLikeUrlForTopicAnchors,
  mergeTopicTaggedPagesByUrl,
  rowOverlapsFocalAnchorTopics,
  normalizeTopicSlug,
  normalizeUrlForCompare,
  SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT,
  SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT,
  SUPPORTING_PAGE_MIN_RELEVANCE_SCORE,
  uniqueParentDiagnosticSlugs,
  type DomainTopicUrlRow
} from '../../utils/topic-supporting-pages';
import {
  splitSectionTextByAbsentTerms,
  type SectionCompareHighlightSegment
} from '../../utils/section-compare-highlight';
import {
  buildSupportingPagesUnifiedColumns,
  buildSupportingPagesV2OnlyColumns,
  hasListInventoryGaps,
  hasMissingSectionGaps,
  type SupportingPagesUnifiedColumn
} from '../../utils/supporting-pages-unified-columns';
import {
  attachSupportingPageSimilarityMatches,
  buildLexicalByNormalizedUrl,
  collectSupportingPageUrls,
  formatSupportingPageTokenPreview,
  PageLexicalFields,
  SUPPORTING_PAGE_SIMILARITY_MIN_SCORE
} from '../../utils/supporting-page-similarity';
import {
  buildOnpageTechnicalIssueViews,
  type OnpageIssueView
} from '../../utils/url-detail-onpage-issues';
import { catchError, forkJoin, map, of, Subscription, switchMap, take } from 'rxjs';

const DEMAND_DIAGNOSTICS_INFO: Record<string, string> = {
  'demand-and-realization-gap':
    'Dev-only breakdown of accountable demand vs how much visibility this URL still leaves on the table.',
  'demand-labeling':
    'Stored claim vs what rankings already associate with the page, plus ambient context when they disagree.',
  'realization-gap':
    'Health band and gap math comparing realized visibility to a keyword-backed demand ceiling.',
  claim: 'Subtopic demand this URL is accountable for—not the whole cluster or plan group.',
  'intent-tokens':
    'Slug/title modifiers (how, template, vs, etc.) that gate which keywords count toward the ceiling.',
  'claim-source': 'Where the claim came from and how confident we are (high, low, or none).',
  'claim-detail': 'Plain-language note from the labeling rule that chose this claim.',
  'market-realization': 'Best stored ranked keyword already aligned with the claim, if any.',
  'realization-source': 'Source for the realization line and its confidence.',
  'ambient-topic': 'Strongest on-page topic that conflicts with the claim; context only, not the claim.',
  'ambient-source': 'Source for the ambient topic signal and its confidence.',
  'split-reasons': 'Why claim, realization, or ambient topic diverge on this URL.',
  computed: 'When demand labels were last written for this URL.',
  'health-band':
    'Critical for HTTP errors; Weak, OK, or Best from scored gap; Unscored when gap is not scored.',
  'health-model': 'Realization-gap thresholds when scored; Unscored when gap is not scored; Eligibility for HTTP failures.',
  'health-reasons': 'Short rule lines showing which checks set the current band.',
  'gap-gate': 'Whether ratio and gap are scored, pending on confidence or immaterial ceiling, or withheld.',
  'realized-visibility':
    'ETV when rankings are missing or coherent with rank proxy; otherwise claim-aligned keyword traffic at actual positions.',
  'demand-ceiling': 'Max upside from aligned ranked keywords at the configured target rank.',
  'ceiling-used': 'Ceiling after floors—the denominator for the realization ratio.',
  'realization-ratio': 'Realized visibility divided by ceiling used; blank when gap is not scored.',
  gap: 'Ceiling used minus realized visibility; blank when gap is not scored.',
  'ceiling-driver': 'Aligned keyword with the largest upside at the target rank.',
  'driver-volume': 'Monthly search volume for the ceiling-driver keyword.',
  'driver-match-tokens': 'Claim intent tokens the driver keyword shares.',
  'upside-at-target-rank': 'Estimated traffic at the target rank (volume × CTR) for the driver.',
  'aligned-keywords-in-pool': 'Ranked keywords that pass claim and intent alignment for the ceiling.',
  'ranked-keywords': 'Stored Labs ranked keywords for this URL from the latest analysis snapshot.',
  'secondary-health':
    'When realization gap is unscored, a separate earned-visibility read without a demand ceiling.',
  'secondary-health-band': 'Weak, OK, or Best from claim-aligned earned visibility when secondary scoring applies.',
  'secondary-health-model': 'Whether secondary scoring is held, skipped, or uses earned visibility.',
  'secondary-health-reasons': 'Rule lines for the secondary model only; primary Health stays Unscored.',
  'secondary-unscored-segment': 'Why the primary gap is unscored (market data, ceiling pool, material floor).',
  'recently-updated-hold': 'Content modified within six months; secondary earned-visibility scoring is held.',
};

interface PageRankedKeywordRow {
  keyword: string;
  position: number;
  searchVolume: number | null;
}

/** Per-page topic signal (mirrors backend `TopicSignal`). */
interface PageTopicSignal {
  topicSlug: string;
  label: string;
  weight: number;
  salience: number;
  relativeDensity: number;
}

interface ContentElement {
  type: 'heading' | 'text' | 'link' | 'table';
  level?: number; // for headings (h1, h2, etc.)
  content: string;
  href?: string; // for links
  tableData?: any[]; // for table content
}

interface CompetitorTopicMatch {
  domain: string;
  authorityScore: number;
  pageCount: number;
  matchedParentTopicSlug: string;
}

/** Marked competitor ahead on a topic overlapping this page (for Improvements v1). */
interface ImprovementCompetitorLead {
  domain: string;
  authorityScore: number;
  pageCount: number;
  topicLabel: string;
  topicSlug: string;
}

interface CompetitorTopicUrlPreview {
  url: string;
  score: number;
  salience?: number;
  estimatedOrganicTraffic?: number;
}

interface CompetitorTopicTooltipState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  urls: CompetitorTopicUrlPreview[];
}

interface TopicOwnershipDomain {
  domain: string;
  isYourSite: boolean;
  rows: DomainTopicUrlRow[];
}

interface TopicOwnershipCompareOption {
  url: string;
  label: string;
  seoIntentScore: number;
}

interface SerpCompetitionMarkedAbove {
  position: number;
  domain: string;
  url: string;
  title: string;
}

interface SerpCompetitionTopLeader {
  position: number;
  domain: string;
  url: string;
  title: string;
  descriptionPreview: string;
  publishedLabel: string | null;
}

interface SerpCompetitionInsight {
  yourPosition: number | null;
  markedAboveYou: boolean;
  markedCompetitorsAbove: SerpCompetitionMarkedAbove[];
  topLeaders: SerpCompetitionTopLeader[];
}

export type UrlDetailEmbedPreset = 'planV2';

@Component({
  selector: 'app-url-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, InventoryQuickCompareDrawerComponent, PlanV2IssueCardActionsComponent],
  templateUrl: './url-detail.html',
  styleUrl: './url-detail.css'
})
export class UrlDetailComponent implements OnInit, OnChanges, OnDestroy {
  /** When set with `embedPreset`, renders URL focus cards inline (e.g. Plan v2 expand) instead of full page chrome. */
  @Input() embedUrl: string | null = null;
  @Input() embedPreset: UrlDetailEmbedPreset | null = null;
  /** Plan v2 expand: hide “all clear” cards (green diagnosis chips). Signals stay when present. */
  @Input() embedActionsOnly = false;
  @Input() embedPlanV2Domain: string | null = null;
  @Input() embedPlanV2Row: ActionPlanV2Row | null = null;
  /** Plan v2 Issues drawer: full-height embed (no 70vh cap). */
  @Input() embedPlanV2InDrawer = false;

  @HostBinding('class.url-detail--embed-plan-v2')
  get hostEmbedPlanV2Class(): boolean {
    return this.isPlanV2Embed();
  }

  @HostBinding('class.url-detail--embed-plan-v2-drawer')
  get hostEmbedPlanV2DrawerClass(): boolean {
    return this.isPlanV2Embed() && this.embedPlanV2InDrawer;
  }

  url: string | null = null;
  pageUrlId = '';
  planClusters: string[] = [];
  pageSignals: InventoryPageSignal[] = [];
  pageSignalsLoading = false;
  private cannibalSignalRows: ActionPlanCannibalizationRow[] = [];
  private internalLinkSignalRows: ActionPlanInternalLinkRow[] = [];
  private unclusteredSignalRows: ActionPlanUnclusteredPageRow[] = [];
  private signalsSubscription: Subscription | null = null;
  analysisData: any = null;
  pageDemand: PageDemandSnapshot | null = null;
  pageRealization: PageRealizationSnapshot | null = null;
  pageHealth: PageHealthSnapshot | null = null;
  pageSecondaryHealth: SecondaryPageHealthSnapshot | null = null;
  pageSerp: PageSerpSnapshot | null = null;
  pageIntentDiagnosis: PageIntentDiagnosis | null = null;
  /** Vocabulary chip hover — highlights folded matches in winner snippets. */
  hoveredVocabChip: string | null = null;
  markedCompetitorDomains: string[] = [];
  urlSerpRefreshRunning = false;
  urlSerpForceRefresh = false;
  urlSerpRefreshProgress: UrlSerpRefreshProgress | null = null;
  pageRefetchRunning = false;
  pageRefetchStatus: string | null = null;
  private pageRefetchPollTimer: ReturnType<typeof setTimeout> | null = null;
  serpSectionExpanded = false;
  pageTopicsExpanded = false;
  topicOwnershipStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  topicOwnershipDomains: TopicOwnershipDomain[] = [];
  /** Per-domain expand for supporting-page tables (dev view). */
  private topicOwnershipPagesExpandedByDomain = new Set<string>();
  /** User view: one expand control for the whole supporting-pages card. */
  topicOwnershipUserExpanded = false;
  topicOwnershipCompareUrl = '';
  private topicOwnershipLoadGen = 0;
  /** True when no precomputed snapshot exists — build from Crawl Management (or dev rebuild). */
  topicOwnershipCacheMiss = false;
  topicOwnershipSnapshotGeneratedAt: string | null = null;
  supportingPagesV1RebuildRunning = false;
  supportingPagesOppRebuildRunning = false;
  supportingPagesRebuildStatus: string | null = null;
  /** Supporting-page opportunity scoring (embedding + section gaps). */
  topicOwnershipV2Status: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  topicOwnershipV2Data: SupportingPagesV2EvalData | null = null;
  topicOwnershipV2Error: string | null = null;
  topicOwnershipV2DetailUrl = '';
  topicOwnershipV2CoveredElsewhereUrl = '';
  topicOwnershipV2ListInventoryUrl = '';
  topicOwnershipV2TextUrl = '';
  topicOwnershipV2CalUrl = '';
  /** Collapsed side-by-side section compare (competitorUrl + sectionNumber); shown by default. */
  private topicOwnershipV2SectionCompareCollapsedKeys = new Set<string>();
  /** Compare detail filter for section coverage rows. */
  topicOwnershipV2CompareFilter: 'not_on_page' | 'gaps' | 'all' = 'not_on_page';
  private topicOwnershipV2LoadGen = 0;
  private supportingPageLexicalByUrl = new Map<string, PageLexicalFields>();
  /** Focal-page anchor topics — narrows your-site matcher pools (dev + opportunity scoring). */
  focalAnchorTopicSlugs = new Set<string>();
  focalAnchorTopicLabels: string[] = [];
  /** Documented in topic-supporting-pages.ts — exposed for template hint. */
  readonly supportingPageMinRelevance = SUPPORTING_PAGE_MIN_RELEVANCE_SCORE;
  readonly supportingPageSimilarityMin = SUPPORTING_PAGE_SIMILARITY_MIN_SCORE;
  analysisStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  contentSectionCollapsed: boolean = true;
  linksSectionCollapsed: boolean = true;
  /** Expanded on-page issue detail rows (key: `critical:0` / `warning:1`). */
  private technicalIssueDetailsExpanded = new Set<string>();
  technicalCriticalComposing = false;
  topicOwnershipComposing = false;
  topicOwnershipComposeState: TopicOwnershipComposeState | null = null;
  internalLinksComposing = false;
  @ViewChild('internalLinksSection') internalLinksSectionRef?: ElementRef<HTMLElement>;
  technicalWarningsComposing = false;
  contentElements: ContentElement[] = [];
  
  // Link data
  internalLinks: any[] = [];
  linkFilter: 'all' | 'structural' | 'contextual' | 'same_page' = 'all';
  linkSort: 'type' | 'confidence' | 'anchor' | 'url' = 'type';
  competitorCoverageStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  competitorCoverageMessage: string = '';
  private competitorTopicMatchesBySlug: Record<string, CompetitorTopicMatch[]> = {};
  private competitorTopicTooltipCache: Record<string, CompetitorTopicTooltipState> = {};
  private activeCompetitorTooltipKey: string | null = null;
  private competitorTooltipHideTimer: ReturnType<typeof setTimeout> | null = null;

  quickCompareOpen = false;
  quickComparePrimaryUrl: string | null = null;
  quickCompareCompareUrl: string | null = null;

  planV2NextAction: ActionPlanV2Row | null = null;
  planV2NextActionLoading = false;
  planV2NextActionError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataForSEOService: DataForSEOService,
    private compareContextService: CompareContextService,
    private domainTopicsService: DomainTopicsService,
    private featureFlags: FeatureFlagService,
    private urlSerpSync: UrlSerpSyncService,
    private inventoryPlanClusters: InventoryPlanClustersService,
    private domainContext: DomainContextService,
    readonly planV2IssueQueue: PlanV2IssueQueueService,
    private readonly planV2EmbedContext: PlanV2EmbedContextService
  ) {}

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  /** User-mode columns/links layout (also used for Plan v2 embed regardless of dev flag). */
  useTopicOwnershipUserLayout(): boolean {
    return !this.isDevView() || this.isPlanV2Embed();
  }

  isActionPlanV2Enabled(): boolean {
    return this.featureFlags.isEnabled('actionPlanV2');
  }

  isEmbedMode(): boolean {
    return this.embedPreset === 'planV2';
  }

  isPlanV2Embed(): boolean {
    return this.embedPreset === 'planV2';
  }

  embedActionsOnlyActive(): boolean {
    return this.isPlanV2Embed() && this.embedActionsOnly;
  }

  private embedDiagnosisIsGreen(chipClass: string): boolean {
    return chipClass.includes('--ok');
  }

  private embedDiagnosisIsActionable(chipClass: string): boolean {
    return chipClass.includes('--warn') || chipClass.includes('--neutral');
  }

  showEmbedMiniOverviewSection(): boolean {
    return this.isPlanV2Embed() && !this.embedActionsOnlyActive();
  }

  showEmbedContentSection(): boolean {
    if (!this.isPlanV2Embed()) return true;
    if (!this.embedActionsOnlyActive()) return true;
    return this.showEmbedSupportingPagesCard();
  }

  showEmbedSupportingPagesCard(): boolean {
    let base = true;
    if (this.isPlanV2Embed()) {
      if (!this.embedActionsOnlyActive()) base = true;
      else if (this.topicOwnershipStatus === 'loading' || this.topicOwnershipV2Status === 'loading') {
        base = true;
      } else if (this.topicOwnershipV2Status === 'ready' && (this.topicOwnershipV2Data?.rows?.length ?? 0) > 0) {
        base = this.hasSupportingPagesActionableGap();
      } else if (this.topicOwnershipStatus !== 'ready' || !this.topicOwnershipDomains.length) {
        base = false;
      } else {
        base = this.hasSupportingPagesActionableGap();
      }
    }
    return this.embedIssueVisible(this.planV2IssueKey('topic_ownership'), base);
  }

  showEmbedFreshnessSection(): boolean {
    if (!this.showContentFreshnessCard()) return false;
    let base = true;
    if (this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.freshnessDiagnosisClass());
    }
    return this.embedIssueVisible(this.planV2IssueKey('freshness'), base);
  }

  showEmbedSerpCompetitionCard(): boolean {
    let base = Boolean(this.hasPageSerpSnapshot() && this.serpCompetitionInsight());
    if (base && this.embedActionsOnlyActive()) {
      base = this.embedDiagnosisIsActionable(this.serpCompetitionChipClass());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_competition'), base);
  }

  showEmbedSerpPhraseCard(): boolean {
    let base = this.showSerpPhraseMatchCard();
    if (base && this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.serpPhraseMatchActionChipClass());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_phrase'), base);
  }

  showEmbedSerpDifficultyCard(): boolean {
    let base = true;
    if (this.embedActionsOnlyActive()) {
      base = this.embedDiagnosisIsActionable(this.serpDifficultyActionChipClass());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_difficulty'), base);
  }

  showEmbedSerpIntentCard(): boolean {
    let base = this.hasPageIntentDiagnosis();
    if (base && this.embedActionsOnlyActive()) {
      base = !this.embedDiagnosisIsGreen(this.pageIntentMatchActionChipClass());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_intent'), base);
  }

  showEmbedSerpAnyCard(): boolean {
    return (
      this.showEmbedSerpCompetitionCard() ||
      this.showEmbedSerpPhraseCard() ||
      this.showEmbedSerpDifficultyCard() ||
      this.showEmbedSerpIntentCard()
    );
  }

  showEmbedSerpSection(): boolean {
    if (!this.isPlanV2Embed() || !this.embedActionsOnlyActive()) return true;
    if (!this.hasPageSerpSnapshot()) return true;
    return this.showEmbedSerpAnyCard();
  }

  showEmbedSerpResultsTable(): boolean {
    if (!this.serpSectionExpanded || !this.hasPageSerpSnapshot()) return false;
    if (!this.embedActionsOnlyActive()) return true;
    return this.showEmbedSerpAnyCard();
  }

  showEmbedTechnicalIssuesCard(): boolean {
    const base = !this.embedActionsOnlyActive() || this.hasTechnicalCriticalIssues();
    return this.embedIssueVisible(this.planV2IssueKey('technical_critical'), base);
  }

  showEmbedTechnicalWarningsCard(): boolean {
    const base = !this.embedActionsOnlyActive() || this.hasTechnicalWarnings();
    return this.embedIssueVisible(this.planV2IssueKey('technical_warnings'), base);
  }

  showEmbedTechnicalSection(): boolean {
    if (!this.isPlanV2Embed() || !this.embedActionsOnlyActive()) return true;
    return this.showEmbedTechnicalIssuesCard() || this.showEmbedTechnicalWarningsCard();
  }

  showEmbedSignalsSection(): boolean {
    let base = true;
    if (this.isPlanV2Embed() && this.embedActionsOnlyActive()) {
      if (this.pageSignalsLoading) base = true;
      else base = this.hasNonInternalLinkSignals();
    }
    return this.embedIssueVisible(this.planV2IssueKey('signals'), base);
  }

  /** Issues drawer: own focus card (same send animation as Technical). */
  showEmbedInternalLinksSection(): boolean {
    if (!this.isPlanV2Embed()) return false;
    const key = this.planV2IssueKey('internal_link');
    if (
      this.planV2IssueQueue.isCardExiting(key, 'work_item') ||
      this.planV2IssueQueue.isCardExiting(key, 'dismissed')
    ) {
      return true;
    }
    if (this.pageSignalsLoading) return false;
    return Boolean(this.internalLinksIssuesPayload());
  }

  embedActionsOnlyEmpty(): boolean {
    if (!this.embedActionsOnlyActive() || this.analysisStatus !== 'success' || !this.analysisData) {
      return false;
    }
    return (
      !this.showEmbedMiniOverviewSection() &&
      !this.showEmbedContentSection() &&
      !this.showEmbedFreshnessSection() &&
      !this.showEmbedSerpSection() &&
      !this.showEmbedTechnicalSection() &&
      !this.showEmbedInternalLinksSection() &&
      !this.showEmbedSignalsSection()
    );
  }

  planV2IssueDomain(): string {
    const fromInput = (this.embedPlanV2Domain || '').trim();
    if (fromInput) return fromInput.toLowerCase().replace(/^www\./, '');
    return this.extractDomainFromUrl(this.url || '');
  }

  planV2IssueKey(issueType: PlanV2IssueType): string {
    return buildPlanV2IssueKey((this.pageUrlId || '').trim(), issueType);
  }

  private embedIssueVisible(issueKey: string, baseVisible: boolean): boolean {
    if (!baseVisible) return false;
    if (!this.isPlanV2Embed()) return baseVisible;
    return this.planV2IssueQueue.isVisibleInIssuesEmbed(issueKey);
  }

  planV2CardExitClass(issueKey: string): Record<string, boolean> {
    return {
      'plan-v2-card-exit-work': this.planV2IssueQueue.isCardExiting(issueKey, 'work_item'),
      'plan-v2-card-exit-dismiss': this.planV2IssueQueue.isCardExiting(issueKey, 'dismissed')
    };
  }

  buildTopicOwnershipIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'topic_ownership'),
      urlId,
      url,
      issueType: 'topic_ownership',
      cardTitle: 'Supporting pages',
      changeSummary: 'Close topic coverage gaps on competitors (add or strengthen supporting URLs).',
      why: this.hasTopicOwnershipCoverageGap()
        ? 'Competitors have relevant supporting URLs without a strong match on your site.'
        : this.topicOwnershipGapChipLabel(),
      diagnosisLabel: this.showTopicOwnershipGapChip() ? this.topicOwnershipGapChipLabel() : null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  needsWorkItemCompose(issueType: PlanV2IssueType): boolean {
    return (
      issueType === 'serp_phrase' ||
      issueType === 'topic_ownership' ||
      issueType === 'internal_link' ||
      issueType === 'technical_critical' ||
      issueType === 'technical_warnings'
    );
  }

  enrichWorkItemPayload(payload: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    if (payload.issueType === 'serp_phrase') {
      return composeSerpPhraseWorkItem(
        payload,
        this.pageIntentDiagnosis,
        this.pageSerpTargetQueryLabel()
      );
    }
    if (payload.issueType === 'internal_link') {
      const pageUrlId = this.pageUrlId.trim();
      const legacyRow = this.internalLinkRowForPayload(payload);
      if (legacyRow) {
        return composeInternalLinkWorkItem(payload, legacyRow, pageUrlId);
      }
      const options = listInternalLinkComposeOptions(
        this.internalLinkSignalRows,
        pageUrlId,
        internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
      );
      return composeInternalLinksWorkItem(payload, options, pageUrlId);
    }
    if (payload.issueType === 'freshness') {
      return composeFreshnessWorkItem(payload, this.freshnessWorkItemContext());
    }
    if (payload.issueType === 'serp_intent') {
      return composeSerpIntentWorkItem(payload, this.pageIntentDiagnosis);
    }
    return payload;
  }

  private freshnessWorkItemContext() {
    return buildFreshnessWorkItemContext({
      lastContentModified: this.analysisData?.lastContentModified ?? null,
      serpResults: this.pageSerp?.results ?? null
    });
  }

  beginTopicOwnershipCompose(): boolean {
    const state = buildTopicOwnershipComposeState({
      domains: this.topicOwnershipDomains,
      v2Rows: this.topicOwnershipV2Data?.rows,
      extractDomain: (pageUrl) => this.extractDomainFromUrl(pageUrl),
      displaySegmentsForSection: (sc) => this.topicOwnershipV2SectionDisplayHeadingSegments(sc)
    });
    if (!state) return false;
    this.topicOwnershipComposeState = state;
    if (state.activeCompetitorUrl) {
      this.topicOwnershipV2DetailUrl = state.activeCompetitorUrl;
      this.topicOwnershipV2CoveredElsewhereUrl = '';
      this.topicOwnershipV2ListInventoryUrl = '';
    }
    return true;
  }

  endTopicOwnershipCompose(): void {
    this.topicOwnershipComposeState = null;
    this.topicOwnershipV2DetailUrl = '';
  }

  getTopicOwnershipComposeState(): TopicOwnershipComposeState | null {
    return this.topicOwnershipComposeState;
  }

  topicOwnershipComposeHasSelection(): boolean {
    return topicOwnershipComposeHasSelection(this.topicOwnershipComposeState);
  }

  finalizeTopicOwnershipComposeWorkItem(base: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    return composeTopicOwnershipWorkItemFromState(base, this.topicOwnershipComposeState);
  }

  onTopicOwnershipComposingChange(composing: boolean): void {
    this.topicOwnershipComposing = composing;
    if (!composing) {
      this.endTopicOwnershipCompose();
    }
  }

  topicOwnershipComposeHasPageGaps(): boolean {
    return (this.topicOwnershipComposeState?.pageGaps.length ?? 0) > 0;
  }

  topicOwnershipComposeHasSectionGaps(): boolean {
    return (this.topicOwnershipComposeState?.sectionCompetitors.length ?? 0) > 0;
  }

  topicOwnershipComposeHasToolGaps(): boolean {
    return (this.topicOwnershipComposeState?.listCompetitors.length ?? 0) > 0;
  }

  topicOwnershipComposeToolsForActiveCompetitor() {
    const state = this.topicOwnershipComposeState;
    const url = state?.activeListCompetitorUrl?.trim();
    if (!state || !url) return [];
    const key = normalizeUrlForCompare(url);
    return state.toolGaps.filter((tool) => normalizeUrlForCompare(tool.competitorUrl) === key);
  }

  onTopicOwnershipComposeListCompetitorChange(competitorUrl: string): void {
    if (!this.topicOwnershipComposeState) return;
    this.topicOwnershipComposeState.activeListCompetitorUrl = competitorUrl;
  }

  topicOwnershipComposeActiveV2Row(): SupportingPagesV2RowResult | null {
    const url = this.topicOwnershipComposeState?.activeCompetitorUrl?.trim();
    if (!url) return null;
    return (
      this.topicOwnershipV2Data?.rows?.find(
        (row) => normalizeUrlForCompare(row.competitorUrl) === normalizeUrlForCompare(url)
      ) ?? null
    );
  }

  onTopicOwnershipComposeCompetitorChange(competitorUrl: string): void {
    if (!this.topicOwnershipComposeState) return;
    this.topicOwnershipComposeState.activeCompetitorUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = competitorUrl;
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
  }

  topicOwnershipComposeSectionRows(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    return this.topicOwnershipV2SectionCoverageGroups(row);
  }

  isTopicOwnershipComposeHeadingSelected(
    row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    segIndex: number
  ): boolean {
    const id = topicOwnershipHeadingComposeId(row.competitorUrl, sc, segIndex);
    return this.topicOwnershipComposeState?.selectedHeadingIds.has(id) ?? false;
  }

  topicOwnershipHeadingTreeContext(
    v2Row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    composeSelect: boolean
  ) {
    return {
      $implicit: this.topicOwnershipV2SectionDisplayHeadingSegments(sc),
      composeSelect,
      v2Row,
      sc
    };
  }

  /** User view defaults sections to expanded compare; compose matches that. */
  topicOwnershipV2SectionCompareExpanded(
    competitorUrl: string,
    sc: SectionCoverageRow,
    composeSelect = false
  ): boolean {
    if (composeSelect && this.topicOwnershipV2SectionCompareAvailable(sc)) return true;
    return this.isTopicOwnershipV2SectionFullTextExpanded(competitorUrl, sc);
  }

  toggleTopicOwnershipComposeHeading(
    event: Event,
    row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    segIndex: number
  ): void {
    if (!this.topicOwnershipComposing || !this.topicOwnershipComposeState) return;
    if (event instanceof KeyboardEvent && event.key === ' ') {
      event.preventDefault();
    }
    event.stopPropagation();
    const id = topicOwnershipHeadingComposeId(row.competitorUrl, sc, segIndex);
    const next = this.topicOwnershipComposeState.selectedHeadingIds;
    if (next.has(id)) next.delete(id);
    else next.add(id);
  }

  getTechnicalIssueComposeOptions(
    issueType: 'technical_critical' | 'technical_warnings'
  ): TechnicalIssueComposeOption[] {
    const kind = issueType === 'technical_critical' ? 'critical' : 'warning';
    const issues =
      kind === 'critical' ? this.technicalCriticalIssueViews() : this.technicalWarningIssueViews();
    return listTechnicalIssueComposeOptions(issues, kind);
  }

  finalizeTechnicalIssuesWorkItem(
    base: PlanV2IssueCardPayload,
    issueType: 'technical_critical' | 'technical_warnings',
    selected: TechnicalIssueComposeOption[]
  ): PlanV2IssueCardPayload {
    const severity = issueType === 'technical_critical' ? 'critical' : 'warning';
    return composeTechnicalIssuesWorkItem(base, selected, severity);
  }

  getInternalLinkComposeOptions(): InternalLinkComposeOption[] {
    return listInternalLinkComposeOptions(
      this.internalLinkSignalRows,
      this.pageUrlId.trim(),
      internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
    );
  }

  finalizeInternalLinksWorkItem(
    base: PlanV2IssueCardPayload,
    selected: InternalLinkComposeOption[]
  ): PlanV2IssueCardPayload[] {
    return composeInternalLinkWorkItemsFromSelection(base, selected);
  }

  private syncPlanV2EmbedHost(): void {
    this.planV2EmbedContext.registerHost(this.isPlanV2Embed() ? this : null);
  }

  buildFreshnessIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const verdict = this.contentFreshnessVerdict();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'freshness'),
      urlId,
      url,
      issueType: 'freshness',
      cardTitle: 'Content freshness',
      changeSummary: 'Refresh page content to align with SERP freshness expectations.',
      why: verdict || this.freshnessDiagnosisLabel(),
      diagnosisLabel: this.freshnessDiagnosisLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpCompetitionIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_competition'),
      urlId,
      url,
      issueType: 'serp_competition',
      cardTitle: "Who's beating you?",
      changeSummary: 'Improve rankings vs marked competitors on this target query.',
      why: `Your rank: ${this.serpCompetitionYourRankLabel()}. ${this.serpCompetitionChipLabel()}.`,
      diagnosisLabel: this.serpCompetitionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpPhraseIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_phrase'),
      urlId,
      url,
      issueType: 'serp_phrase',
      cardTitle: 'SERP phrase match',
      changeSummary: 'Align page copy with winning SERP vocabulary (missing or misaligned phrases).',
      why: this.serpPhraseMatchActionChipLabel(),
      diagnosisLabel: this.showSerpPhraseMatchActionChip() ? this.serpPhraseMatchActionChipLabel() : null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpDifficultyIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const hint = this.serpKeywordDifficultyHint();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_difficulty'),
      urlId,
      url,
      issueType: 'serp_difficulty',
      cardTitle: 'SERP difficulty',
      changeSummary: 'Account for keyword difficulty when prioritizing effort on this query.',
      why: hint ? `${this.serpKeywordDifficultyShort()} — ${hint}` : this.serpKeywordDifficultyShort(),
      diagnosisLabel: this.serpDifficultyActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpIntentIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const lines = this.pageIntentBriefLines();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_intent'),
      urlId,
      url,
      issueType: 'serp_intent',
      cardTitle: 'Intent vs. your page',
      changeSummary: 'Align page format and angle with dominant SERP intent.',
      why: lines.length ? lines.join(' ') : this.pageIntentMatchActionChipLabel(),
      diagnosisLabel: this.pageIntentMatchActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildTechnicalCriticalIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const titles = this.technicalCriticalIssueViews().map((i) => i.title).slice(0, 4);
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'technical_critical'),
      urlId,
      url,
      issueType: 'technical_critical',
      cardTitle: 'Technical issues',
      changeSummary: 'Fix critical on-page / technical issues listed for this URL.',
      why: titles.length ? titles.join(' · ') : this.technicalIssuesActionChipLabel(),
      diagnosisLabel: this.technicalIssuesActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildTechnicalWarningsIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const titles = this.technicalWarningIssueViews().map((i) => i.title).slice(0, 4);
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'technical_warnings'),
      urlId,
      url,
      issueType: 'technical_warnings',
      cardTitle: 'Technical warnings',
      changeSummary: 'Resolve on-page warnings to reduce crawl and ranking friction.',
      why: titles.length ? titles.join(' · ') : this.technicalWarningsActionChipLabel(),
      diagnosisLabel: this.technicalWarningsActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  hasNonInternalLinkSignals(): boolean {
    return this.pageSignals.some((s) => s.kind !== 'internal_link');
  }

  buildSignalsIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url || !this.hasNonInternalLinkSignals()) return null;
    const labels = this.pageSignals.filter((s) => s.kind !== 'internal_link').map((s) => s.label);
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'signals'),
      urlId,
      url,
      issueType: 'signals',
      cardTitle: 'Workspace signals',
      changeSummary: 'Resolve open cannibalization or cluster signals for this URL.',
      why: labels.join(' · '),
      diagnosisLabel: null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildInternalLinksSectionPayload(options?: { includeLinksInWorkItems?: boolean }): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    const rows = options?.includeLinksInWorkItems
      ? this.pageSignalInternalLinkDetails()
      : this.pageSignalInternalLinkDetailsPendingTriage();
    if (!urlId || !url || rows.length === 0) return null;
    const count = rows.length;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'internal_link'),
      urlId,
      url,
      issueType: 'internal_link',
      cardTitle: 'Internal link suggestions',
      changeSummary: `Add ${count} contextual internal link${count === 1 ? '' : 's'}.`,
      why: 'Other pages where you can add a link pointing here.',
      diagnosisLabel: null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  /** Shown in Issues embed while in queue or playing send/dismiss exit (same as other focus cards). */
  internalLinksIssuesPayload(): PlanV2IssueCardPayload | null {
    if (!this.isPlanV2Embed()) return null;
    const urlId = (this.pageUrlId || '').trim();
    const key = buildPlanV2IssueKey(urlId, 'internal_link');
    const exiting =
      this.planV2IssueQueue.isCardExiting(key, 'work_item') ||
      this.planV2IssueQueue.isCardExiting(key, 'dismissed');
    const payload = this.buildInternalLinksSectionPayload({ includeLinksInWorkItems: exiting });
    if (!payload) return null;
    if (exiting) return payload;
    return this.planV2IssueQueue.isVisibleInIssuesEmbed(key) ? payload : null;
  }

  internalLinkRowForPayload(payload: PlanV2IssueCardPayload): ActionPlanInternalLinkRow | null {
    const key =
      (payload.suggestionKey || '').trim() ||
      payload.issueKey.split(':').slice(2).join(':').trim();
    if (!key) return null;
    return (
      this.internalLinkSignalRows.find((r) => r.suggestionKey === key && r.status === 'open') || null
    );
  }

  /** Triage buttons only when the card reflects a real problem (not green / all-clear chips). */
  showIssueCardActions(payload: PlanV2IssueCardPayload | null | undefined): boolean {
    if (!payload || !this.isPlanV2Embed()) return false;
    if (!this.planV2IssueQueue.isVisibleInIssuesEmbed(payload.issueKey)) return false;
    return this.isIssuePayloadActionable(payload);
  }

  isIssuePayloadActionable(payload: PlanV2IssueCardPayload): boolean {
    switch (payload.issueType) {
      case 'topic_ownership':
        return this.hasSupportingPagesActionableGap();
      case 'freshness':
        return !this.embedDiagnosisIsGreen(this.freshnessDiagnosisClass());
      case 'serp_competition':
        return false;
      case 'serp_phrase':
        return !this.embedDiagnosisIsGreen(this.serpPhraseMatchActionChipClass());
      case 'serp_difficulty':
        return false;
      case 'serp_intent':
        return !this.embedDiagnosisIsGreen(this.pageIntentMatchActionChipClass());
      case 'technical_critical':
        return this.hasTechnicalCriticalIssues();
      case 'technical_warnings':
        return this.hasTechnicalWarnings();
      case 'signals':
        return this.hasNonInternalLinkSignals();
      case 'internal_link':
        return this.pageSignalInternalLinkDetailsPendingTriage().length > 0;
      default:
        return false;
    }
  }

  openPlanV2InInventory(): void {
    this.router.navigate(['/inventory'], { queryParams: { section: 'planV2' } });
  }

  hasDemandDiagnostics(): boolean {
    return Boolean(this.pageDemand || this.pageRealization || this.pageHealth || this.pageSecondaryHealth);
  }

  hasRankedKeywords(): boolean {
    return this.rankedKeywordsRows().length > 0;
  }

  rankedKeywordsRows(): PageRankedKeywordRow[] {
    const keywords = this.analysisData?.rankedKeywords?.keywords;
    if (!Array.isArray(keywords)) return [];

    return keywords
      .map((row: any) => ({
        keyword: String(row?.keyword || '').trim(),
        position: Number(row?.position),
        searchVolume: Number.isFinite(row?.searchVolume) ? Number(row.searchVolume) : null
      }))
      .filter((row: PageRankedKeywordRow) => row.keyword && Number.isFinite(row.position))
      .sort((a: PageRankedKeywordRow, b: PageRankedKeywordRow) => {
        if (a.position !== b.position) return a.position - b.position;
        return a.keyword.localeCompare(b.keyword);
      });
  }

  rankedKeywordsFetchedAt(): string | null {
    const fetchedAt = this.analysisData?.rankedKeywords?.fetchedAt;
    if (!fetchedAt) return null;
    return String(fetchedAt);
  }

  formatRankedKeywordVolume(value: number | null): string {
    if (!Number.isFinite(value)) return '—';
    return String(Math.round(value as number));
  }

  trackByRankedKeyword(_index: number, row: PageRankedKeywordRow): string {
    return `${row.keyword}:${row.position}`;
  }

  demandDiagnosticsInfo(key: string): string {
    return DEMAND_DIAGNOSTICS_INFO[key] ?? '';
  }

  pageDemandIntentTokensText(): string {
    const tokens = this.pageDemand?.claimIntentTokens;
    if (!Array.isArray(tokens) || tokens.length === 0) return '—';
    return tokens.join(', ');
  }

  pageDemandSourceConfidence(side: 'claim' | 'realization' | 'ambientTopic'): string {
    if (!this.pageDemand) return '—';
    if (side === 'claim') return formatPageDemandSourceConfidence(this.pageDemand.claim);
    if (side === 'realization') return formatPageDemandSourceConfidence(this.pageDemand.realization);
    return formatPageDemandSourceConfidence(this.pageDemand.ambientTopic);
  }

  pageHealthLabel(): string {
    return this.pageHealthForDisplay().band
      ? pageHealthBandLabel(this.pageHealthForDisplay().band)
      : '—';
  }

  pageHealthClass(): string {
    return this.pageHealthForDisplay().band
      ? pageHealthBandClass(this.pageHealthForDisplay().band)
      : '';
  }

  private pageHealthForDisplay() {
    if (this.pageHealth) return this.pageHealth;
    return classifyPageHealth({
      statusCode: this.analysisData?.statusCode ?? 0,
      estimatedOrganicTraffic: this.analysisData?.estimatedOrganicTraffic ?? null,
      realization: this.pageRealization
    });
  }

  effectiveHealthBand(): EffectiveHealthDisplayBand {
    const primary = this.pageHealthForDisplay().band;
    return resolveEffectiveHealthBand(primary, this.pageSecondaryHealth);
  }

  effectiveHealthLabel(): string {
    return effectiveHealthBandLabel(
      this.pageHealthForDisplay().band,
      this.pageSecondaryHealth
    );
  }

  effectiveHealthClass(): string {
    return effectiveHealthBandClass(
      this.pageHealthForDisplay().band,
      this.pageSecondaryHealth
    );
  }

  improvementsSummary(): string {
    const claim = this.pageDemand?.claim?.label?.trim();
    const band = this.effectiveHealthBand();
    const etv = this.analysisData?.estimatedOrganicTraffic;
    const etvText = Number.isFinite(etv) ? `~${Math.round(etv)} est. traffic/mo` : 'no est. traffic yet';

    if (this.analysisStatus === 'loading') {
      return 'Loading health and demand context for this URL…';
    }
    if (this.analysisStatus === 'error' || !this.analysisData) {
      return 'Run a crawl and analysis for this URL to see improvements.';
    }

    const claimPhrase = claim ? `accountable for “${claim}”` : 'without a confident demand claim yet';

    if (band === 'critical') {
      return `This URL is not eligible for organic performance (${claimPhrase}). Fix availability before SEO improvements.`;
    }
    if (band === 'recently_updated') {
      return `Recently updated — realization gap is on hold. ${claimPhrase}; ${etvText}. Earned visibility may still be building.`;
    }
    if (band === 'unscored') {
      return `Gap not scored yet — ${claimPhrase}; ${etvText}. See drivers below; compare with marked competitors when data allows.`;
    }
    if (band === 'weak') {
      return `Under-realized vs demand — ${claimPhrase}; ${etvText}. There is material upside if rankings move toward the demand ceiling.`;
    }
    if (band === 'best') {
      return `Near ceiling for scored demand — ${claimPhrase}; ${etvText}. Focus on defending position and SERP depth vs competitors.`;
    }
    return `Pulling reasonable weight for demand — ${claimPhrase}; ${etvText}.`;
  }

  improvementsWhyBullets(): string[] {
    const bullets: string[] = [];
    const primary = this.pageHealthForDisplay();

    if (primary.reasons?.length) {
      bullets.push(...primary.reasons);
    }

    if (primary.band === 'unscored' && this.pageSecondaryHealth?.reasons?.length) {
      for (const reason of this.pageSecondaryHealth.reasons) {
        if (!bullets.includes(reason)) bullets.push(reason);
      }
    }

    if (this.pageDemand?.claim?.label) {
      bullets.push(`Demand claim: ${this.pageDemand.claim.label}`);
    }

    const realization = this.pageDemand?.realization;
    if (realization?.label) {
      const pos = realization.position !== null ? ` · pos ${realization.position}` : '';
      bullets.push(`Best aligned rank: ${realization.label}${pos}`);
    }

    const gapGate = this.pageRealization?.gapGate;
    if (gapGate && gapGate !== 'scored') {
      bullets.push(`Realization gap: ${gapGate.replace(/_/g, ' ')}`);
    } else if (this.pageRealization?.gapGate === 'scored') {
      const ratio = this.pageRealization.realizationRatio;
      const gap = this.pageRealization.gap;
      if (ratio !== null && gap !== null) {
        bullets.push(
          `Realized ${this.formatRealizationNumber(this.pageRealization.realizedVisibility)} vs ceiling ${this.formatRealizationNumber(this.pageRealization.ceiling)} (ratio ${this.formatRealizationRatio(ratio)}, gap ${this.formatRealizationNumber(gap)})`
        );
      }
    }

    if (this.pageSecondaryHealth?.model === 'freshness_hold') {
      bullets.push('Secondary scoring on hold — content updated within the last six months');
    }

    if (this.pageSecondaryHealth?.unscoredSegment) {
      bullets.push(this.secondaryUnscoredSegmentLabel());
    }

    return bullets.slice(0, 8);
  }

  /** Strongest topic signal on this page (same order as the topics table). */
  primaryPageTopicSignal(): PageTopicSignal | null {
    const signals = this.getPageTopicSignals();
    return signals.length ? signals[0] : null;
  }

  /**
   * Topic used for Improvements competitor list: primary signal first;
   * if no marked competitors overlap that slug, fall back to the next signal that has matches.
   */
  improvementCompetitorTopicSignal(): PageTopicSignal | null {
    const signals = this.getPageTopicSignals();
    if (!signals.length) return null;

    for (const signal of signals) {
      if (this.getCompetitorTopicMatches(signal).length > 0) {
        return signal;
      }
    }
    return signals[0];
  }

  improvementCompetitorUsesFallbackTopic(): boolean {
    const primary = this.primaryPageTopicSignal();
    const used = this.improvementCompetitorTopicSignal();
    if (!primary || !used) return false;
    return primary.topicSlug !== used.topicSlug;
  }

  improvementCompetitorTopicLabel(): string {
    return this.improvementCompetitorTopicSignal()?.label || 'this page';
  }

  improvementCompetitorLeads(): ImprovementCompetitorLead[] {
    const signal = this.improvementCompetitorTopicSignal();
    if (!signal) return [];

    return this.getCompetitorTopicMatches(signal)
      .map((match) => ({
        domain: match.domain,
        authorityScore: match.authorityScore,
        pageCount: match.pageCount,
        topicLabel: signal.label,
        topicSlug: signal.topicSlug
      }))
      .sort((a, b) => {
        if (b.authorityScore !== a.authorityScore) return b.authorityScore - a.authorityScore;
        if (b.pageCount !== a.pageCount) return b.pageCount - a.pageCount;
        return a.domain.localeCompare(b.domain);
      })
      .slice(0, 6);
  }

  hasImprovementCompetitorLeads(): boolean {
    return this.improvementCompetitorLeads().length > 0;
  }

  trackImprovementCompetitor(_index: number, lead: ImprovementCompetitorLead): string {
    return `${lead.domain}::${lead.topicSlug}`;
  }

  hasPageSerpSnapshot(): boolean {
    return Boolean(this.pageSerp?.results?.length);
  }

  pageSerpPendingReason(): string | null {
    if (this.pageSerp) return null;
    if (!this.pageDemand?.claim?.label) {
      return 'SERP pending — no demand claim yet. Run page demand labeling after crawl.';
    }
    if (this.pageDemand.claim.confidence === 'none') {
      return 'SERP pending — claim confidence too low to pick a target query.';
    }
    return 'SERP pending — no snapshot for this URL. Refresh URL SERP for the domain (dev).';
  }

  pageSerpTargetQueryLabel(): string {
    const claim = this.pageDemand?.claim?.label?.trim();
    if (claim) return claim;
    return this.pageSerp?.queryDisplay?.trim() || '—';
  }

  pageDemandClaimLabel(): string {
    return this.pageDemand?.claim?.label?.trim() || '—';
  }

  pageDemandClaimClass(): string {
    const confidence = this.pageDemand?.claim?.confidence;
    if (confidence === 'low' || confidence === 'none') return 'url-focus-stat-claim--low';
    if (confidence === 'medium') return 'url-focus-stat-claim--medium';
    return 'url-focus-stat-claim--high';
  }

  pageDemandClaimTitle(): string {
    const demand = this.pageDemand;
    if (!demand?.claim) return 'No demand claim stored for this URL.';
    const parts = [
      `Claim: ${demand.claim.label}`,
      `Source: ${demand.claim.source}`,
      `Confidence: ${demand.claim.confidence}`
    ];
    if (demand.claim.detail) parts.push(demand.claim.detail);
    if (demand.realization?.label) {
      parts.push(`Best aligned rank: ${demand.realization.label}`);
      if (demand.realization.position !== null) {
        parts.push(`Position ${demand.realization.position}`);
      }
    }
    return parts.join(' · ');
  }

  rankedKeywordsCountLabel(): string {
    const count = this.rankedKeywordsRows().length;
    return count > 0 ? String(count) : '—';
  }

  rankedKeywordsCountTitle(): string {
    const count = this.rankedKeywordsRows().length;
    const fetched = this.rankedKeywordsFetchedAt();
    if (!count) return 'No ranked keywords on this URL yet.';
    return fetched
      ? `${count} keywords from last ranked-keywords fetch (${fetched})`
      : `${count} keywords from ranked-keywords data`;
  }

  estimatedTrafficLabel(): string {
    const etv = this.analysisData?.estimatedOrganicTraffic;
    if (!Number.isFinite(etv)) return '—';
    return `~${Math.round(Number(etv))}/mo`;
  }

  estimatedTrafficTitle(): string {
    const etv = this.analysisData?.estimatedOrganicTraffic;
    if (!Number.isFinite(etv)) return 'No estimated organic traffic on this analysis.';
    return `DataForSEO estimated organic traffic: ~${Math.round(Number(etv))} visits per month`;
  }

  pageRealizationStatLabel(): string {
    const r = this.pageRealization;
    if (!r) return '—';
    if (r.gapGate !== 'scored') {
      return this.realizationGapGateLabel().replace(/_/g, ' ');
    }
    const gap = formatInventoryRealizationNumber(r.gap);
    const ratio = formatInventoryRealizationRatio(r.realizationRatio);
    return `${gap} · ${ratio}`;
  }

  pageRealizationStatTitle(): string {
    const r = this.pageRealization;
    if (!r) return 'No realization gap data.';
    if (r.gapGate !== 'scored') {
      return `Gap not scored (${r.gapGate}). ${inventoryRealizationGapTitle(r)}`;
    }
    return `${inventoryRealizationGapTitle(r)} · ${inventoryRealizationRatioTitle(r)}`;
  }

  pageContentLastModifiedDate(): Date | null {
    return parsePageContentLastModifiedDate(this.analysisData?.lastContentModified);
  }

  pageContentLastModifiedLabel(): string {
    return pageContentLastModifiedLabel(
      this.pageContentLastModifiedDate(),
      this.analysisData?.lastContentModified
    );
  }

  private serpWinnerPublishedDates(): Date[] {
    return serpWinnerPublishedDatesFromResults(this.pageSerp?.results);
  }

  serpWinnersPublishedDateSummary(): { count: number; typicalLabel: string } | null {
    const dates = this.serpWinnerPublishedDates().sort((a, b) => a.getTime() - b.getTime());
    if (dates.length < 2) return null;
    const median = medianPublishedDate(dates);
    if (!median) return null;
    return {
      count: dates.length,
      typicalLabel: `Typical ${formatFreshnessDateLabel(median)}`
    };
  }

  showContentFreshnessCard(): boolean {
    if (this.analysisStatus !== 'success' || !this.analysisData) return false;
    return Boolean(this.pageContentLastModifiedDate() || this.serpWinnersPublishedDateSummary());
  }

  toggleSerpSectionExpanded(): void {
    this.serpSectionExpanded = !this.serpSectionExpanded;
  }

  serpSectionExpandLabel(): string {
    return this.serpSectionExpanded ? 'Hide top 10' : 'Show top 10';
  }

  freshnessDiagnosisLabel(): string {
    return resolveFreshnessDiagnosisLabel(
      this.pageContentLastModifiedDate(),
      this.serpWinnerPublishedDates()
    );
  }

  freshnessDiagnosisClass(): string {
    const label = this.freshnessDiagnosisLabel();
    if (label === 'Stale' || label === 'Behind SERP' || label === 'Aging') {
      return 'url-focus-diagnosis-chip--warn';
    }
    if (label === 'No date') return 'url-focus-diagnosis-chip--muted';
    if (label === 'Ahead of SERP' || label === 'Recent') return 'url-focus-diagnosis-chip--ok';
    return 'url-focus-diagnosis-chip--neutral';
  }

  hasTopicOwnershipCoverageGap(): boolean {
    for (const block of this.topicOwnershipDomains) {
      if (block.isYourSite) continue;
      for (const row of block.rows) {
        if (!row.similarOnYourSite) return true;
      }
    }
    return false;
  }

  hasSupportingPagesSectionGaps(): boolean {
    return hasMissingSectionGaps(this.topicOwnershipV2Data?.rows);
  }

  hasSupportingPagesListInventoryGaps(): boolean {
    return hasListInventoryGaps(this.topicOwnershipV2Data?.rows);
  }

  hasSupportingPagesActionableGap(): boolean {
    return (
      this.hasTopicOwnershipCoverageGap() ||
      this.hasSupportingPagesSectionGaps() ||
      this.hasSupportingPagesListInventoryGaps()
    );
  }

  showTopicOwnershipGapChip(): boolean {
    return (
      (this.topicOwnershipStatus === 'ready' && this.topicOwnershipDomains.length > 0) ||
      (this.topicOwnershipV2Status === 'ready' && (this.topicOwnershipV2Data?.rows?.length ?? 0) > 0)
    );
  }

  supportingPagesColumnsReady(): boolean {
    return this.getSupportingPagesColumns().length > 0 && !this.supportingPagesUnifiedLoading();
  }

  topicOwnershipGapChipLabel(): string {
    return this.hasSupportingPagesActionableGap() ? 'GAP FOUND' : 'NO GAP';
  }

  topicOwnershipGapChipClass(): string {
    return this.hasSupportingPagesActionableGap()
      ? 'url-focus-diagnosis-chip--warn'
      : 'url-focus-diagnosis-chip--ok';
  }

  supportingPagesUnifiedLoading(): boolean {
    return this.topicOwnershipStatus === 'loading' || this.topicOwnershipV2Status === 'loading';
  }

  getSupportingPagesColumns(): SupportingPagesUnifiedColumn[] {
    const v2Rows = this.topicOwnershipV2Data?.rows;
    if (this.topicOwnershipStatus === 'ready' && this.topicOwnershipDomains.length) {
      return buildSupportingPagesUnifiedColumns({
        domains: this.topicOwnershipDomains,
        focalUrl: this.url || '',
        v2Rows,
        extractDomain: (pageUrl) => this.extractDomainFromUrl(pageUrl),
        filterRows: (rows) =>
          this.getTopicOwnershipRowsForDisplay({ domain: '', isYourSite: false, rows })
      });
    }
    if (this.topicOwnershipV2Status === 'ready' && v2Rows?.length) {
      return buildSupportingPagesV2OnlyColumns({
        focalUrl: this.url || '',
        yourSiteDomain: this.extractDomainFromUrl(this.url || '') || 'Your site',
        v2Rows,
        extractDomain: (pageUrl) => this.extractDomainFromUrl(pageUrl)
      });
    }
    return [];
  }

  topicOwnershipHeroLinkTitle(col: SupportingPagesUnifiedColumn): string {
    if (col.isYourSite) return col.heroUrl;
    const row = col.heroRow || { url: col.heroUrl, seoIntentScore: 0, topics: [] };
    return this.topicOwnershipLinkTitle(
      { domain: col.domain, isYourSite: false, rows: col.supportingRows },
      row
    );
  }

  topicOwnershipSupportingLinkTitle(col: SupportingPagesUnifiedColumn, row: DomainTopicUrlRow): string {
    return this.topicOwnershipLinkTitle(
      { domain: col.domain, isYourSite: col.isYourSite, rows: col.supportingRows },
      row
    );
  }

  getVisibleSupportingRows(col: SupportingPagesUnifiedColumn): DomainTopicUrlRow[] {
    if (this.isTopicOwnershipPagesExpanded(col.domain)) return col.supportingRows;
    return col.supportingRows.slice(0, SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
  }

  hasMoreSupportingRowsInColumn(col: SupportingPagesUnifiedColumn): boolean {
    return col.supportingRows.length > SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT;
  }

  supportingRowsExpandLabel(col: SupportingPagesUnifiedColumn): string {
    if (this.isTopicOwnershipPagesExpanded(col.domain)) return 'Show fewer pages';
    const hidden = Math.max(0, col.supportingRows.length - SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
    return hidden > 0 ? `Show more pages (${hidden} more)` : 'Show more pages';
  }

  topicOwnershipV2RowForColumn(col: SupportingPagesUnifiedColumn): SupportingPagesV2RowResult | null {
    return col.v2Row;
  }

  topicOwnershipV2MissingSectionsChipLabel(row: SupportingPagesV2RowResult): string {
    const count = row.missingSectionCount ?? 0;
    if (count === 1) return '1 missing section';
    return `${count} missing sections`;
  }

  topicOwnershipV2CoveredElsewhereChipLabel(row: SupportingPagesV2RowResult): string {
    return row.coveredElsewhereUrl ? 'Covered elsewhere' : 'Not covered elsewhere';
  }

  topicOwnershipV2CoveredElsewhereChipTitle(row: SupportingPagesV2RowResult): string {
    if (row.coveredElsewhereUrl) {
      return `${row.coveredElsewhereLabel || 'Covered elsewhere'}: ${row.coveredElsewhereUrl}`;
    }
    return 'No similar supporting page found elsewhere on your site for this competitor URL.';
  }

  topicOwnershipV2HasListInventory(row: SupportingPagesV2RowResult): boolean {
    return !!(
      row.listInventoryDiff?.detected && (row.listInventoryDiff.competitorOnly?.length ?? 0) > 0
    );
  }

  topicOwnershipV2ListInventoryChipLabel(row: SupportingPagesV2RowResult): string {
    const count = row.listInventoryDiff?.competitorOnly?.length ?? 0;
    if (count === 1) return '1 tool on their list, not on yours';
    return `${count} tools on their list, not on yours`;
  }

  topicOwnershipV2ListInventoryChipTitle(row: SupportingPagesV2RowResult): string {
    const diff = row.listInventoryDiff;
    if (!diff?.detected) return '';
    const missing = (diff.competitorOnly || []).map((item) => item.label).join(', ');
    return `Roundup list diff — on their list but not mentioned on your page: ${missing}`;
  }

  isTopicOwnershipV2ListInventoryOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2ListInventoryUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2ListInventory(competitorUrl: string): void {
    if (this.topicOwnershipV2ListInventoryUrl === competitorUrl) {
      this.topicOwnershipV2ListInventoryUrl = '';
      return;
    }
    this.topicOwnershipV2ListInventoryUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
  }

  isTopicOwnershipV2CompareOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2DetailUrl === v2.competitorUrl;
  }

  /** Missing-sections compare panel — rendered full-width below domain columns. */
  getOpenSectionCompareV2Row(): SupportingPagesV2RowResult | null {
    const detailUrl = this.topicOwnershipV2DetailUrl?.trim();
    if (!detailUrl) return null;
    const row = this.topicOwnershipV2Data?.rows?.find(
      (r) => normalizeUrlForCompare(r.competitorUrl) === normalizeUrlForCompare(detailUrl)
    );
    if (!row || !this.topicOwnershipV2HasSectionCoverage(row)) return null;
    return row;
  }

  isTopicOwnershipV2CoveredElsewhereOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2CoveredElsewhereUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2CoveredElsewhere(competitorUrl: string): void {
    if (this.topicOwnershipV2CoveredElsewhereUrl === competitorUrl) {
      this.topicOwnershipV2CoveredElsewhereUrl = '';
      return;
    }
    this.topicOwnershipV2CoveredElsewhereUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
  }

  topicOwnershipCompareOptions(): TopicOwnershipCompareOption[] {
    const seen = new Set<string>();
    const options: TopicOwnershipCompareOption[] = [];
    for (const block of this.topicOwnershipDomains) {
      if (block.isYourSite) continue;
      for (const row of block.rows) {
        const normalized = normalizeUrlForCompare(row.url);
        if (!normalized || seen.has(normalized)) continue;
        seen.add(normalized);
        const path = this.topicOwnershipPagePath(row.url);
        const traffic = this.topicOwnershipPageTraffic(row.estimatedOrganicTraffic);
        const label = traffic ? `${block.domain} · ${path} (${traffic})` : `${block.domain} · ${path}`;
        options.push({
          url: row.url,
          label,
          seoIntentScore: row.seoIntentScore
        });
      }
    }
    return options.sort((a, b) => b.seoIntentScore - a.seoIntentScore);
  }

  openQuickCompareWithUrl(compareUrl: string): void {
    const primaryUrl = this.normalizeNavigableUrl(this.url || '');
    const normalizedCompare = this.normalizeNavigableUrl(compareUrl);
    if (!primaryUrl || !normalizedCompare) return;
    this.quickComparePrimaryUrl = primaryUrl;
    this.quickCompareCompareUrl = normalizedCompare;
    this.quickCompareOpen = true;
  }

  onTopicOwnershipCompareSelected(competitorUrl: string): void {
    if (!competitorUrl?.trim()) return;
    this.openQuickCompareWithUrl(competitorUrl);
    this.topicOwnershipCompareUrl = '';
  }

  technicalIssuesActionChipLabel(): string {
    return this.hasTechnicalCriticalIssues() ? 'ISSUES FOUND' : 'NO ISSUES';
  }

  technicalIssuesActionChipClass(): string {
    return this.hasTechnicalCriticalIssues()
      ? 'url-focus-diagnosis-chip--warn'
      : 'url-focus-diagnosis-chip--ok';
  }

  technicalWarningsActionChipLabel(): string {
    return this.hasTechnicalWarnings() ? 'WARNINGS FOUND' : 'NO WARNINGS';
  }

  technicalWarningsActionChipClass(): string {
    return this.hasTechnicalWarnings()
      ? 'url-focus-diagnosis-chip--warn'
      : 'url-focus-diagnosis-chip--ok';
  }

  pageIntentMatchActionChipLabel(): string {
    const d = this.pageIntentDiagnosis;
    if (!d || d.serpFamily === 'unknown') return '—';
    if (d.serpFamily === 'mixed') return 'MIXED SERP';
    return d.intentAlignedWithSerp ? 'MATCH' : 'NO MATCH';
  }

  pageIntentMatchActionChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d || d.serpFamily === 'unknown') return 'url-focus-diagnosis-chip--muted';
    if (d.serpFamily === 'mixed') return 'url-focus-diagnosis-chip--neutral';
    return d.intentAlignedWithSerp
      ? 'url-focus-diagnosis-chip--ok'
      : 'url-focus-diagnosis-chip--warn';
  }

  showSerpPhraseMatchActionChip(): boolean {
    if (!this.pageIntentDiagnosis) return false;
    return (
      this.hasLexicalVocabChips() ||
      this.showFragmentedSerpVocabularyNote() ||
      (this.pageIntentDiagnosis.missingTerms?.length ?? 0) > 0 ||
      (this.pageIntentDiagnosis.misalignedLexicalTerms?.length ?? 0) > 0
    );
  }

  serpPhraseMatchActionChipLabel(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return '—';
    const missing = d.missingTerms?.length ?? 0;
    const misaligned = d.misalignedLexicalTerms?.length ?? 0;
    if (missing === 0 && misaligned === 0) {
      return this.showFragmentedSerpVocabularyNote() ? 'REVIEW PHRASES' : 'ALIGNED';
    }
    const parts: string[] = [];
    if (missing > 0) parts.push('MISSING PHRASES');
    if (misaligned > 0) parts.push('WRONG CONTEXT');
    return parts.join(' · ');
  }

  serpPhraseMatchActionChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'url-focus-diagnosis-chip--muted';
    const missing = d.missingTerms?.length ?? 0;
    const misaligned = d.misalignedLexicalTerms?.length ?? 0;
    if (missing === 0 && misaligned === 0) {
      return this.showFragmentedSerpVocabularyNote()
        ? 'url-focus-diagnosis-chip--neutral'
        : 'url-focus-diagnosis-chip--ok';
    }
    return 'url-focus-diagnosis-chip--warn';
  }

  serpDifficultyBand(): 'easy' | 'medium' | 'hard' | 'unknown' {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return 'unknown';
    const n = Number(kd);
    if (n < 15) return 'easy';
    if (n <= 25) return 'medium';
    return 'hard';
  }

  serpDifficultyActionChipLabel(): string {
    const band = this.serpDifficultyBand();
    if (band === 'unknown') return '—';
    return band.toUpperCase();
  }

  serpDifficultyActionChipClass(): string {
    const band = this.serpDifficultyBand();
    if (band === 'easy') return 'url-focus-diagnosis-chip--ok';
    if (band === 'medium') return 'url-focus-diagnosis-chip--neutral';
    if (band === 'hard') return 'url-focus-diagnosis-chip--warn';
    return 'url-focus-diagnosis-chip--muted';
  }

  pageIntentBriefLines(): string[] {
    const d = this.pageIntentDiagnosis;
    if (!d) return [];
    const lines: string[] = [];
    const serpLabel = this.pageIntentFamilyLabel(d.serpFamily);
    const yoursLabel = this.pageIntentFamilyLabel(d.bestSegmentFamily);

    if (d.serpFamily === 'mixed') {
      if (!(d.serpMixedIntentSamples?.length >= 2)) {
        lines.push('Top results disagree on page type — pick one angle.');
      } else {
        lines.push('Pick one angle to align with.');
      }
    } else if (d.intentAlignedWithSerp) {
      lines.push(`SERP and your page both read as ${serpLabel}.`);
    } else if (d.bestSegmentFamily && d.bestSegmentFamily !== 'unknown' && d.serpFamily !== 'unknown') {
      lines.push(`SERP expects ${serpLabel}; strongest section reads ${yoursLabel}.`);
    } else if (d.serpFamily !== 'unknown') {
      lines.push(`SERP mostly ${serpLabel}.`);
    }

    const extra = d.bullets.find(
      (b) =>
        b.kind === 'placement' ||
        b.kind === 'lexical_gap' ||
        b.kind === 'lexical_misuse' ||
        b.kind === 'vocabulary_fragmented'
    );
    if (extra && lines.length < 2) {
      const text = extra.text.length > 140 ? `${extra.text.slice(0, 137)}…` : extra.text;
      lines.push(text);
    }
    return lines.slice(0, 2);
  }

  showSerpPhraseMatchCard(): boolean {
    if (!this.pageIntentDiagnosis) return false;
    return (
      this.hasPageIntentCompare() ||
      this.hasLexicalVocabChips() ||
      this.showFragmentedSerpVocabularyNote()
    );
  }

  serpKeywordDifficultyShort(): string {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return '—';
    return `${Math.round(Number(kd))}`;
  }

  /** Shown only when KD is missing — not the scale/source line. */
  serpKeywordDifficultyHint(): string | null {
    if (!Number.isFinite(this.pageSerp?.keywordDifficulty)) {
      return 'Refresh SERP to load keyword difficulty.';
    }
    return null;
  }

  contentFreshnessVerdict(): string | null {
    return comparePageFreshnessToSerp(
      this.pageContentLastModifiedDate(),
      this.serpWinnerPublishedDates()
    ).verdict;
  }

  pageSerpQuerySourceLabel(): string {
    const source = this.pageSerp?.querySource;
    if (source === 'realization') return 'Realization keyword';
    if (source === 'ceiling_driver') return 'Ceiling driver';
    if (source === 'claim_fallback') return 'Claim fallback';
    return '—';
  }

  pageSerpFetchedLabel(): string {
    if (!this.pageSerp?.fetchedAt) return '—';
    try {
      return new Date(this.pageSerp.fetchedAt).toLocaleString();
    } catch {
      return this.pageSerp.fetchedAt;
    }
  }

  pageSerpKeywordDifficultyLabel(): string | null {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return null;
    const source =
      this.pageSerp?.keywordDifficultyDataSource === 'api' ? 'Labs API' : 'cached';
    return `Keyword difficulty ${Math.round(Number(kd))}/100 (${source})`;
  }

  pageSerpKeywordDifficultyTitle(): string {
    return (
      'DataForSEO Labs keyword difficulty: how hard it is to rank in the top 10 for this target query ' +
      '(0 = easy, 100 = very hard). Updated on URL SERP refresh. Not per-domain authority.'
    );
  }

  pageSerpYourPresenceLabel(): string {
    if (!this.pageSerp) return '—';
    if (this.pageSerp.yourPosition === null) return 'Not in top 10';
    const match =
      this.pageSerp.matchType === 'exact_url'
        ? 'exact URL'
        : this.pageSerp.matchType === 'domain'
          ? 'domain match'
          : 'match';
    return `#${this.pageSerp.yourPosition} (${match})`;
  }

  pageSerpResultCompetitorKind(row: PageSerpResultRow): 'you' | 'marked' | 'unknown' {
    const activeDomain = this.extractDomainFromUrl(this.url || '');
    if (activeDomain && row.domain === activeDomain) return 'you';
    if (this.markedCompetitorDomains.includes(row.domain)) return 'marked';
    return 'unknown';
  }

  pageSerpResultCompetitorLabel(row: PageSerpResultRow): string {
    const kind = this.pageSerpResultCompetitorKind(row);
    if (kind === 'you') return 'You';
    if (kind === 'marked') return 'Marked competitor';
    return 'Unknown';
  }

  pageSerpRowIntentFamily(row: PageSerpResultRow): string {
    const hit = this.pageIntentDiagnosis?.serpRowIntents?.find((i) => i.position === row.position);
    return hit?.family || 'unknown';
  }

  pageSerpRowIntentLabel(row: PageSerpResultRow): string {
    return this.pageIntentFamilyLabel(this.pageSerpRowIntentFamily(row));
  }

  trackPageSerpRow(_index: number, row: PageSerpResultRow): string {
    return `${row.position}::${row.domain}::${row.url}`;
  }

  pageSerpSnippetSegments(row: PageSerpResultRow): SerpSnippetSegment[] {
    return buildSerpSnippetSegments(row.description || '', row.highlighted);
  }

  /** Top-10 rows whose snippet contains the hovered chip (winners compare pane). */
  pageSerpCompareWinnerRows(): PageSerpResultRow[] {
    if (!this.hoveredVocabChip || !this.pageSerp?.results?.length) return [];
    return this.pageSerp.results.filter((row) => this.pageSerpRowHasVocabChipMatch(row));
  }

  pageSerpCompareWinnerRowSegments(row: PageSerpResultRow): SerpSnippetSegment[] {
    if (!this.hoveredVocabChip) return [];
    return buildSerpFoldedTermSegments(row.description || '', this.hoveredVocabChip, foldLexicalToken);
  }

  pageSerpRowHasVocabChipMatch(row: PageSerpResultRow): boolean {
    if (!this.hoveredVocabChip || !row.description?.trim()) return false;
    return buildSerpFoldedTermSegments(row.description, this.hoveredVocabChip, foldLexicalToken).some(
      (s) => s.emphasized
    );
  }

  hasPageIntentDiagnosis(): boolean {
    return (this.pageIntentDiagnosis?.bullets?.length || 0) > 0;
  }

  hasPageIntentCompare(): boolean {
    const d = this.pageIntentDiagnosis;
    if (!d) return false;
    return Boolean(d.yourBestSection?.text) || (d.serpWinnerExcerpts?.length || 0) > 0;
  }

  pageIntentContractTerms(): string[] {
    return this.pageIntentDiagnosis?.serpLexicalTerms || [];
  }

  onVocabChipHover(term: string | null): void {
    this.hoveredVocabChip = term;
  }

  isVocabChipHovered(term: string): boolean {
    return this.hoveredVocabChip === term;
  }

  pageIntentVocabChipTitle(term: string): string {
    if (this.hoveredVocabChip === term) {
      return `Showing “${term}” in matching top-10 snippets (right pane)`;
    }
    return `Hover to preview “${term}” in top results (plural-aware)`;
  }

  pageIntentYourSectionSegments(text: string): SerpSnippetSegment[] {
    if (this.hoveredVocabChip) {
      return buildSerpFoldedTermSegments(text, this.hoveredVocabChip, foldLexicalToken);
    }
    return buildYourPageVocabularySegments(
      text,
      this.pageIntentContractTerms(),
      this.pageIntentDiagnosis?.serpCollocationHeads
    );
  }

  pageIntentMisalignedPhraseLinks(text: string | null | undefined): MisalignedPhraseLink[] {
    if (!text?.trim()) return [];
    return collectMisalignedPhraseLinks(
      text,
      this.pageIntentContractTerms(),
      this.pageIntentDiagnosis?.serpCollocationHeads
    );
  }

  pageIntentMisalignedAriaLabel(seg: SerpSnippetSegment): string {
    const link = seg.misalignedLink;
    if (!link) return `${seg.text}: SERP vocabulary used in the wrong phrase context`;
    return `${link.head} connects to ${link.actual} on your page; winners use ${link.head} near ${link.expected}`;
  }

  pageIntentMisalignedChipTitle(term: string): string {
    const link = this.pageIntentMisalignedLinkForTerm(term);
    if (!link) return 'Used in the wrong phrase context';
    return `Your page: ${link.head} → ${link.actual}. Winners: ${link.head} → ${link.expected}.`;
  }

  private pageIntentMisalignedLinkForTerm(term: string) {
    const d = this.pageIntentDiagnosis;
    const texts = [d?.yourBestSection?.text, d?.yourIntroSection?.text].filter(Boolean) as string[];
    const termFold = term.toLowerCase().replace(/[^a-z0-9]+/g, '');
    for (const text of texts) {
      const link = this.pageIntentMisalignedPhraseLinks(text).find((l) => {
        const headFold = l.head.toLowerCase().replace(/[^a-z0-9]+/g, '');
        return headFold === termFold || headFold.startsWith(termFold) || termFold.startsWith(headFold);
      });
      if (link) return link;
    }
    return undefined;
  }

  hasLexicalVocabChips(): boolean {
    const d = this.pageIntentDiagnosis;
    if (!d) return false;
    return (
      (d.presentLexicalTerms?.length || 0) +
      (d.misalignedLexicalTerms?.length || 0) +
      (d.missingTerms?.length || 0) >
      0
    );
  }

  /** Top snippets lacked a shared repeated-vocabulary contract (chips intentionally omitted). */
  showFragmentedSerpVocabularyNote(): boolean {
    const d = this.pageIntentDiagnosis;
    if (!d) return false;
    const shared =
      d.serpVocabularyShared ?? (d.serpLexicalTerms?.length ?? 0) > 0;
    if (shared) return false;
    return this.hasPageIntentCompare() || this.hasPageIntentDiagnosis();
  }

  pageIntentDiagnosisBulletClass(bullet: PageIntentDiagnosisBullet): string {
    if (bullet.severity === 'success') return 'url-serp-diagnosis-success';
    if (bullet.severity === 'warn') return 'url-serp-diagnosis-warn';
    return 'url-serp-diagnosis-info';
  }

  private static readonly INTENT_FAMILY_LABELS: Record<string, string> = {
    definition: 'definition',
    how_to: 'how-to',
    listicle: 'list / roundup',
    versus: 'versus',
    template: 'template',
    pricing: 'pricing',
    mixed: 'mixed',
    unknown: 'unclear'
  };

  pageIntentFamilyLabel(family: string | null | undefined): string {
    if (!family) return 'unclear';
    return UrlDetailComponent.INTENT_FAMILY_LABELS[family] || family;
  }

  pageIntentYourPageFamilyLabel(): string {
    const family = this.pageIntentDiagnosis?.bestSegmentFamily;
    if (!family || family === 'unknown') return 'unclear';
    return this.pageIntentFamilyLabel(family);
  }

  pageIntentFamilyChipClassFor(family: string | null | undefined): string {
    const f = family || 'unknown';
    if (f === 'mixed' || f === 'unknown') return 'url-serp-intent-chip url-serp-intent-chip-unknown';
    return 'url-serp-intent-chip url-serp-intent-chip-serp';
  }

  pageIntentSerpFamilyChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'url-serp-intent-chip';
    if (d.serpFamily === 'mixed') return 'url-serp-intent-chip url-serp-intent-chip-mixed';
    if (d.serpFamily === 'unknown') return 'url-serp-intent-chip url-serp-intent-chip-unknown';
    if (d.intentAlignedWithSerp) return 'url-serp-intent-chip url-serp-intent-chip-aligned';
    return 'url-serp-intent-chip url-serp-intent-chip-serp';
  }

  pageIntentYourFamilyChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'url-serp-intent-chip url-serp-intent-chip-unknown';
    if (d.intentAlignedWithSerp) return 'url-serp-intent-chip url-serp-intent-chip-aligned';
    if (d.bestSegmentFamily === 'unknown') return 'url-serp-intent-chip url-serp-intent-chip-unknown';
    if (d.serpFamily === 'mixed') return 'url-serp-intent-chip url-serp-intent-chip-mismatch';
    return 'url-serp-intent-chip url-serp-intent-chip-mismatch';
  }

  showPageIntentAlignmentRow(): boolean {
    const d = this.pageIntentDiagnosis;
    return Boolean(d && d.serpFamily && d.serpFamily !== 'unknown');
  }

  pageSerpCompactSummary(): string | null {
    if (!this.pageSerp?.results?.length) return null;
    const presence = this.pageSerpYourPresenceLabel();
    let you = 0;
    let marked = 0;
    let unknown = 0;
    for (const row of this.pageSerp.results) {
      const kind = this.pageSerpResultCompetitorKind(row);
      if (kind === 'you') you += 1;
      else if (kind === 'marked') marked += 1;
      else unknown += 1;
    }
    const parts = [presence];
    if (you) parts.push(`${you} you`);
    if (marked) parts.push(`${marked} marked`);
    if (unknown) parts.push(`${unknown} unknown`);
    return parts.join(' · ');
  }

  pageSerpPublishedDate(row: PageSerpResultRow): Date | null {
    if (!row.publishedAt) return null;
    try {
      const d = new Date(row.publishedAt);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  pageSerpPublishedAgeClass(row: PageSerpResultRow): string {
    const d = this.pageSerpPublishedDate(row);
    if (!d) return '';
    const ageMs = Date.now() - d.getTime();
    const sixMonths = 183 * 24 * 60 * 60 * 1000;
    const twoYears = 730 * 24 * 60 * 60 * 1000;
    if (ageMs < sixMonths) return 'url-serp-date-fresh';
    if (ageMs < twoYears) return 'url-serp-date-aging';
    return 'url-serp-date-stale';
  }

  serpCompetitionInsight(): SerpCompetitionInsight | null {
    if (!this.pageSerp?.results?.length) return null;
    const yourPosition = this.pageSerp.yourPosition;
    const results = [...this.pageSerp.results].sort((a, b) => a.position - b.position);

    const markedCompetitorsAbove: SerpCompetitionMarkedAbove[] = [];
    for (const row of results) {
      if (this.pageSerpResultCompetitorKind(row) !== 'marked') continue;
      if (row.position > 5) continue;
      const beatsYou = yourPosition === null || row.position < yourPosition;
      if (!beatsYou) continue;
      markedCompetitorsAbove.push({
        position: row.position,
        domain: row.domain,
        url: row.url,
        title: row.title
      });
    }

    const markedAboveYou = markedCompetitorsAbove.length > 0;
    const topLeaders: SerpCompetitionTopLeader[] = markedAboveYou
      ? []
      : results
          .filter((row) => row.position >= 1 && row.position <= 3)
          .filter((row) => this.pageSerpResultCompetitorKind(row) !== 'you')
          .map((row) => ({
            position: row.position,
            domain: row.domain,
            url: row.url,
            title: row.title,
            descriptionPreview: this.serpCompetitionDescriptionPreview(row.description),
            publishedLabel: this.serpCompetitionPublishedLabel(row)
          }));

    return {
      yourPosition,
      markedAboveYou,
      markedCompetitorsAbove,
      topLeaders
    };
  }

  serpCompetitionYourRankLabel(): string {
    const pos = this.pageSerp?.yourPosition;
    if (pos === null || pos === undefined) return 'Not in top 10';
    return `#${pos}`;
  }

  serpCompetitionMarkedAboveLabel(): string {
    if (!this.markedCompetitorDomains.length) return '—';
    const insight = this.serpCompetitionInsight();
    if (!insight) return '—';
    return insight.markedAboveYou ? 'Yes' : 'No';
  }

  serpCompetitionChipLabel(): string {
    if (!this.markedCompetitorDomains.length) return 'NO MARKED RIVALS';
    const insight = this.serpCompetitionInsight();
    if (!insight) return '—';
    if (insight.markedAboveYou) return 'COMPETITORS AHEAD';
    if (insight.yourPosition === 1) return 'YOU LEAD';
    return 'NO RIVALS AHEAD';
  }

  serpCompetitionChipClass(): string {
    if (!this.markedCompetitorDomains.length) return 'url-focus-diagnosis-chip--muted';
    const insight = this.serpCompetitionInsight();
    if (!insight) return 'url-focus-diagnosis-chip--muted';
    if (insight.markedAboveYou) return 'url-focus-diagnosis-chip--warn';
    if (insight.yourPosition === 1) return 'url-focus-diagnosis-chip--ok';
    return 'url-focus-diagnosis-chip--neutral';
  }

  private serpCompetitionDescriptionPreview(description: string | undefined): string {
    const text = (description || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    return text.length > 160 ? `${text.slice(0, 157)}…` : text;
  }

  private serpCompetitionPublishedLabel(row: PageSerpResultRow): string | null {
    const d = this.pageSerpPublishedDate(row);
    if (!d) return null;
    try {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  }

  refreshUrlSerpForDomain(): void {
    const pageUrl = this.url || '';
    if (!pageUrl || this.urlSerpRefreshRunning) return;

    this.urlSerpRefreshRunning = true;
    this.urlSerpRefreshProgress = {
      percent: 0,
      keywordsDone: 0,
      totalKeywords: 0,
      statusLine: this.urlSerpForceRefresh
        ? 'Starting URL SERP refresh (force API)…'
        : 'Starting URL SERP refresh (cache-first)…',
      etaLabel: null,
      chunksCompleted: 0,
      runningCostUsd: 0
    };

    void this.urlSerpSync
      .refreshPageUrl(pageUrl, {
        forceRefresh: this.urlSerpForceRefresh,
        onProgress: (p) => {
          this.urlSerpRefreshProgress = p;
        }
      })
      .then(() => {
        this.loadAnalysisData();
        if (this.urlSerpRefreshProgress) {
          this.urlSerpRefreshProgress = {
            ...this.urlSerpRefreshProgress,
            percent: 100,
            etaLabel: 'Done',
            statusLine: 'SERP refresh complete'
          };
        }
      })
      .catch((err) => {
        console.error('Failed to refresh URL SERP snapshots:', err);
      })
      .finally(() => {
        this.urlSerpRefreshRunning = false;
        setTimeout(() => {
          if (!this.urlSerpRefreshRunning) {
            this.urlSerpRefreshProgress = null;
          }
        }, 6000);
      });
  }

  pageHealthModelLabel(): string {
    if (!this.pageHealth) return '—';
    if (this.pageHealth.model === 'realization_gap') return 'Realization gap';
    if (this.pageHealth.model === 'unscored') return 'Unscored';
    if (this.pageHealth.model === 'eligibility') return 'Eligibility';
    return 'Unscored';
  }

  pageSecondaryHealthLabel(): string {
    if (!this.pageSecondaryHealth?.band) {
      if (this.pageSecondaryHealth?.model === 'freshness_hold') return 'Recently updated';
      return '—';
    }
    return pageHealthBandLabel(this.pageSecondaryHealth.band);
  }

  pageSecondaryHealthClass(): string {
    if (!this.pageSecondaryHealth?.band) {
      if (this.pageSecondaryHealth?.model === 'freshness_hold') return 'score-neutral';
      return '';
    }
    return pageHealthBandClass(this.pageSecondaryHealth.band);
  }

  pageSecondaryHealthModelLabel(): string {
    if (!this.pageSecondaryHealth) return '—';
    if (this.pageSecondaryHealth.model === 'earned_visibility') return 'Earned visibility';
    if (this.pageSecondaryHealth.model === 'freshness_hold') return 'Freshness hold';
    return 'None';
  }

  secondaryUnscoredSegmentLabel(): string {
    const segment = this.pageSecondaryHealth?.unscoredSegment;
    if (!segment) return '—';
    if (segment === 'no_claim') return 'No demand claim';
    if (segment === 'low_confidence_claim') return 'Low-confidence demand claim';
    if (segment === 'no_market_data') return 'Awaiting earned market data';
    if (segment === 'no_aligned_ceiling') return 'No claim-aligned demand ceiling';
    return 'Demand ceiling below material upside floor';
  }

  hasSecondaryHealthDiagnostics(): boolean {
    return !!this.pageSecondaryHealth;
  }

  realizationGapGateLabel(): string {
    return this.pageRealization?.gapGate || '—';
  }

  formatRealizationNumber(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '—';
    return Number(value).toFixed(1);
  }

  formatRealizationRatio(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '—';
    return Number(value).toFixed(2);
  }

  ngOnInit(): void {
    this.syncPlanV2EmbedHost();
    if (this.isEmbedMode()) {
      this.url = (this.embedUrl || '').trim() || null;
      if (this.url) this.loadAnalysisData();
      return;
    }
    // Decode inventory route URL param (can be encoded multiple times).
    this.url = this.decodeUrlParam(this.route.snapshot.paramMap.get('url') || '');
    if (this.url) this.loadAnalysisData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embedPreset'] || changes['embedUrl']) {
      this.syncPlanV2EmbedHost();
    }
    if (!this.isEmbedMode() || !changes['embedUrl'] || changes['embedUrl'].firstChange) return;
    const next = (changes['embedUrl'].currentValue || '').trim();
    if (next && next !== this.url) {
      this.url = next;
      this.loadAnalysisData();
    }
  }

  ngOnDestroy(): void {
    this.planV2EmbedContext.registerHost(null);
    this.signalsSubscription?.unsubscribe();
    this.clearPageRefetchPollTimer();
  }

  refetchSinglePageAnalysis(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.pageRefetchRunning) return;

    this.pageRefetchRunning = true;
    this.pageRefetchStatus = 'Starting single-page re-fetch…';
    this.clearPageRefetchPollTimer();

    this.dataForSEOService.refetchSinglePageAnalysis(pageUrl).subscribe({
      next: (res) => {
        const taskId = String(res?.data?.taskId || '').trim();
        const status = String(res?.data?.status || '').trim();
        if (status === 'completed') {
          this.pageRefetchStatus = 'Single-page re-fetch completed. Reloading URL detail…';
          this.pageRefetchRunning = false;
          this.loadAnalysisData();
          return;
        }
        if (status === 'pending' && taskId) {
          this.pageRefetchStatus = 'Re-fetch task queued. Waiting for completion…';
          this.pollSinglePageRefetch(taskId, 1);
          return;
        }
        this.pageRefetchStatus = 'Re-fetch returned no task. Please retry.';
        this.pageRefetchRunning = false;
      },
      error: (error) => {
        this.pageRefetchStatus =
          error?.error?.message || error?.message || 'Failed to start single-page re-fetch.';
        this.pageRefetchRunning = false;
      }
    });
  }

  private pollSinglePageRefetch(taskId: string, attempt: number): void {
    const maxAttempts = 60; // 5 minutes @ 5s intervals
    this.dataForSEOService.getUrlAnalysisStatus(taskId).subscribe({
      next: (res) => {
        const status = String(res?.data?.status || '').trim();
        if (status === 'completed') {
          this.pageRefetchStatus = 'Single-page re-fetch completed. Reloading URL detail…';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          this.loadAnalysisData();
          return;
        }
        if (status === 'failed') {
          this.pageRefetchStatus = res?.data?.error || 'Single-page re-fetch failed.';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          return;
        }
        if (attempt >= maxAttempts) {
          this.pageRefetchStatus = 'Still running in background. You can refresh URL detail in a moment.';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          return;
        }
        this.pageRefetchStatus = `Re-fetch running… (${attempt}/${maxAttempts})`;
        this.pageRefetchPollTimer = setTimeout(() => this.pollSinglePageRefetch(taskId, attempt + 1), 5000);
      },
      error: (error) => {
        this.pageRefetchStatus =
          error?.error?.message || error?.message || 'Failed to poll single-page re-fetch status.';
        this.pageRefetchRunning = false;
        this.clearPageRefetchPollTimer();
      }
    });
  }

  private clearPageRefetchPollTimer(): void {
    if (!this.pageRefetchPollTimer) return;
    clearTimeout(this.pageRefetchPollTimer);
    this.pageRefetchPollTimer = null;
  }

  loadAnalysisData(): void {
    if (!this.url) return;
    
    this.clearPageRefetchPollTimer();
    this.analysisStatus = 'loading';
    this.pageUrlId = '';
    this.planClusters = [];
    this.pageSignals = [];
    this.pageSignalsLoading = false;
    this.cannibalSignalRows = [];
    this.internalLinkSignalRows = [];
    this.unclusteredSignalRows = [];
    this.signalsSubscription?.unsubscribe();
    this.pageTopicsExpanded = false;
    this.topicOwnershipStatus = 'idle';
    this.topicOwnershipDomains = [];
    this.topicOwnershipPagesExpandedByDomain.clear();
    this.topicOwnershipUserExpanded = false;
    this.technicalIssueDetailsExpanded.clear();
    this.dataForSEOService.getUrlAnalysis(this.url).subscribe({
      next: (response) => {
        if (response.success) {
          this.pageUrlId = (response.data.urlId || '').trim();
          this.planClusters = Array.isArray(response.data.planClusters)
            ? response.data.planClusters.filter((c) => typeof c === 'string' && c.trim())
            : [];
          this.analysisData = response.data.analysis;
          this.pageDemand = response.data.pageDemand || null;
          this.pageRealization = response.data.pageRealization || null;
          this.pageHealth = response.data.pageHealth || null;
          this.pageSecondaryHealth = response.data.pageSecondaryHealth || null;
          this.pageSerp = response.data.pageSerp || null;
          this.pageIntentDiagnosis = response.data.pageIntentDiagnosis || null;
          this.analysisStatus = 'success';
          this.loadPageSignals();
          this.loadPlanV2NextAction();
          if (this.analysisData) {
            this.extractContentElements();
            this.extractLinkData();
            this.loadCompetitorTopicCoverage();
            this.loadTopicOwnership();
          }
        } else {
          this.analysisStatus = 'error';
        }
      },
      error: (error) => {
        console.error('Failed to load analysis:', error);
        this.analysisStatus = 'error';
      }
    });
  }

  private loadPlanV2NextAction(): void {
    if (this.isPlanV2Embed()) {
      this.planV2NextAction = null;
      this.planV2NextActionError = null;
      return;
    }
    if (!this.isActionPlanV2Enabled()) {
      this.planV2NextAction = null;
      this.planV2NextActionError = null;
      return;
    }
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!domain || !urlId) {
      this.planV2NextAction = null;
      return;
    }
    this.planV2NextActionLoading = true;
    this.planV2NextActionError = null;
    this.inventoryPlanClusters.getActionPlanV2(domain, { urlId, pageSize: 1 }).subscribe({
      next: (res) => {
        this.planV2NextAction = res?.data?.rows?.[0] ?? null;
        this.planV2NextActionLoading = false;
      },
      error: (err) => {
        this.planV2NextAction = null;
        this.planV2NextActionLoading = false;
        this.planV2NextActionError = err?.error?.message || 'Could not load next action.';
      }
    });
  }

  private loadPageSignals(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!domain || !urlId) {
      this.pageSignals = buildPageSignalsForUrl(urlId, this.planClusters, [], [], []);
      this.pageSignalsLoading = false;
      return;
    }

    this.pageSignalsLoading = true;
    this.signalsSubscription?.unsubscribe();
    this.signalsSubscription = forkJoin({
      cannibal: this.inventoryPlanClusters
        .getCannibalizationActionPlan(domain, {
          limit: 200,
          includeCompleted: false,
          includeDismissed: false,
          includePageIntersection: false
        })
        .pipe(catchError(() => of(null))),
      internalLinks: this.inventoryPlanClusters
        .getInternalLinkActionPlan(domain, {
          urlId,
          limit: 500,
          includeCompleted: false,
          includeDismissed: false
        })
        .pipe(catchError(() => of(null))),
      unclustered: this.inventoryPlanClusters
        .getUnclusteredPageActionPlan(domain, {
          limit: 200,
          includeCompleted: false,
          includeDismissed: false
        })
        .pipe(catchError(() => of(null)))
    }).subscribe({
      next: (res) => {
        this.cannibalSignalRows = Array.isArray(res.cannibal?.data?.rows) ? res.cannibal.data.rows : [];
        this.internalLinkSignalRows = Array.isArray(res.internalLinks?.data?.rows)
          ? res.internalLinks.data.rows
          : [];
        this.unclusteredSignalRows = Array.isArray(res.unclustered?.data?.rows) ? res.unclustered.data.rows : [];
        this.pageSignals = buildPageSignalsForUrl(
          urlId,
          this.planClusters,
          this.cannibalSignalRows.map((row) => ({
            status: row.status,
            loserUrlId: row.loserUrlId,
            urlAId: row.urlAId,
            urlBId: row.urlBId
          })),
          this.internalLinkSignalRows.map((row) => ({
            status: row.status,
            sourceUrlId: row.sourceUrlId,
            targetUrlId: row.targetUrlId
          })),
          this.unclusteredSignalRows.map((row) => ({
            status: row.status,
            urlId: row.urlId
          }))
        );
        this.pageSignalsLoading = false;
      },
      error: () => {
        this.pageSignals = buildPageSignalsForUrl(urlId, this.planClusters, [], [], []);
        this.pageSignalsLoading = false;
      }
    });
  }

  pageSignalChipClass(kind: InventoryPageSignalKind): string {
    return `page-signal-chip--${kind}`;
  }

  hasPageSignalKind(kind: InventoryPageSignalKind): boolean {
    return this.pageSignals.some((s) => s.kind === kind);
  }

  pageSignalCannibalDetails(): ActionPlanCannibalizationRow[] {
    const urlId = this.pageUrlId.trim();
    if (!urlId) return [];
    return this.cannibalSignalRows.filter(
      (row) =>
        row.status === 'open' &&
        (row.loserUrlId === urlId || row.urlAId === urlId || row.urlBId === urlId)
    );
  }

  pageSignalInternalLinkDetails(): ActionPlanInternalLinkRow[] {
    return filterInternalLinksIncomingToPage(this.internalLinkSignalRows, this.pageUrlId.trim());
  }

  /** Incoming links not already sent to Work items (Issues triage on the beneficiary URL). */
  pageSignalInternalLinkDetailsPendingTriage(): ActionPlanInternalLinkRow[] {
    return filterInternalLinksPendingIssuesTriage(
      this.internalLinkSignalRows,
      this.pageUrlId.trim(),
      internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
    );
  }

  pageSignalInternalLinkDetailsForDisplay(): ActionPlanInternalLinkRow[] {
    if (this.isPlanV2Embed()) {
      const exiting =
        this.planV2IssueQueue.isCardExiting(this.planV2IssueKey('internal_link'), 'work_item') ||
        this.planV2IssueQueue.isCardExiting(this.planV2IssueKey('internal_link'), 'dismissed');
      return exiting
        ? this.pageSignalInternalLinkDetails()
        : this.pageSignalInternalLinkDetailsPendingTriage();
    }
    return this.pageSignalInternalLinkDetails();
  }

  internalLinkRowDisplay(row: ActionPlanInternalLinkRow) {
    return buildInternalLinkRowDisplay(row, this.pageUrlId.trim());
  }

  internalLinkDirectionChip(row: ActionPlanInternalLinkRow): string {
    return internalLinkDirectionLabel(this.internalLinkRowDisplay(row).direction);
  }

  internalLinkDirectionChipClasses(row: ActionPlanInternalLinkRow): string {
    return internalLinkDirectionChipClass(this.internalLinkRowDisplay(row).direction);
  }

  internalLinkPlacementParts(row: ActionPlanInternalLinkRow) {
    const sentence = (row.where?.sentence || '').trim();
    const anchor = (row.suggestedAnchor || '').trim();
    return splitPlacementByAnchor(sentence, anchor);
  }

  formatInternalLinkScore(score: number): string {
    return formatInternalLinkRelevanceScore(score);
  }

  onInternalLinksComposingChange(composing: boolean): void {
    this.internalLinksComposing = composing;
    if (!composing) return;
    // List collapses when compose opens; keep the section anchored in the drawer viewport.
    setTimeout(() => this.scrollInternalLinksSectionIntoView(), 0);
  }

  private scrollInternalLinksSectionIntoView(): void {
    const el = this.internalLinksSectionRef?.nativeElement;
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  pageSignalUnclusteredDetail(): ActionPlanUnclusteredPageRow | null {
    const urlId = this.pageUrlId.trim();
    if (!urlId) return null;
    return this.unclusteredSignalRows.find((row) => row.status === 'open' && row.urlId === urlId) || null;
  }

  pageSignalCannibalCounterpart(row: ActionPlanCannibalizationRow): string {
    const urlId = this.pageUrlId.trim();
    if (row.urlAId === urlId) return row.urlB;
    if (row.urlBId === urlId) return row.urlA;
    if (row.loserUrlId === urlId) {
      return row.winnerUrlId === row.urlAId ? row.urlB : row.urlA;
    }
    return row.urlB;
  }

  unclusteredRecommendationLabel(rec: ActionPlanUnclusteredPageRow['recommendation']): string {
    if (rec === 'delete') return 'Delete';
    if (rec === 'cluster_or_delete') return 'Cluster or delete';
    if (rec === 'rewrite_redirect_or_delete') return 'Rewrite, redirect, or delete';
    return rec;
  }

  inventoryDetailLink(targetUrl: string): string[] {
    const trimmed = (targetUrl || '').trim();
    return trimmed ? ['/inventory', encodeURIComponent(trimmed)] : ['/inventory'];
  }

  openInventoryForSignals(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    if (domain) this.domainContext.setSelectedDomain(domain);
  }

  /**
   * Extract key content elements from structured content
   */
  extractContentElements(): void {
    const structuredContent = this.getStructuredContent();
    if (!structuredContent) return;

    this.contentElements = [];
    
    // Process main_topic content
    if (structuredContent.main_topic && Array.isArray(structuredContent.main_topic)) {
      structuredContent.main_topic.forEach((topic: any) => {
        // Add the main heading
        if (topic.h_title) {
          this.contentElements.push({
            type: 'heading',
            level: topic.level || 1,
            content: topic.h_title
          });
        }
        
        // Add primary content
        if (topic.primary_content && Array.isArray(topic.primary_content)) {
          topic.primary_content.forEach((content: any) => {
            if (content.text && content.text.trim()) {
              this.contentElements.push({
                type: 'text',
                content: content.text.trim()
              });
            }
            
            // Add links from the content
            if (content.urls && Array.isArray(content.urls)) {
              content.urls.forEach((url: any) => {
                if (url.anchor_text && url.url) {
                  this.contentElements.push({
                    type: 'link',
                    content: url.anchor_text,
                    href: url.url
                  });
                }
              });
            }
          });
        }
        

      });
    }
    
    // Process header content
    if (structuredContent.header) {
      // Add primary header content
      if (structuredContent.header.primary_content && Array.isArray(structuredContent.header.primary_content)) {
        structuredContent.header.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'heading',
              level: 1,
              content: content.text.trim()
            });
          }
          
          // Add links from header
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
      

    }
    
    // Process footer content
    if (structuredContent.footer) {
      // Add primary footer content only
      if (structuredContent.footer.primary_content && Array.isArray(structuredContent.footer.primary_content)) {
        structuredContent.footer.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'text',
              content: content.text.trim()
            });
          }
          
          // Add links from footer
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
    }
    
    // Process table content
    if (structuredContent.table_content && Array.isArray(structuredContent.table_content)) {
      structuredContent.table_content.forEach((table: any) => {
        if (table.body && Array.isArray(table.body)) {
          table.body.forEach((row: any, rowIndex: number) => {
            if (row.row_cells && Array.isArray(row.row_cells)) {
              // Create a table element with the row data
              this.contentElements.push({
                type: 'table',
                content: `Table Row ${rowIndex + 1}`,
                tableData: row.row_cells.map((cell: any) => ({
                  text: cell.text || '',
                  isHeader: cell.is_header || false,
                  urls: cell.urls || null
                }))
              });
            }
          });
        }
      });
    }
  }

  getScoreClass(score: number): string {
    if (score >= 90) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  }

  technicalOnPageScoreLabel(): string {
    const score = this.analysisData?.onpageScore;
    if (score == null || !Number.isFinite(Number(score))) return '—';
    const n = Number(score);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  technicalOnPageIssuesLabel(): string | null {
    const { critical, warnings } = buildOnpageTechnicalIssueViews(this.analysisData);
    if (critical.length === 0 && warnings.length === 0) return null;
    const parts: string[] = [];
    if (critical.length > 0) parts.push(`${critical.length} critical`);
    if (warnings.length > 0) parts.push(`${warnings.length} warning${warnings.length === 1 ? '' : 's'}`);
    return parts.join(' · ');
  }

  technicalCriticalIssueViews(): OnpageIssueView[] {
    return buildOnpageTechnicalIssueViews(this.analysisData).critical;
  }

  technicalWarningIssueViews(): OnpageIssueView[] {
    return buildOnpageTechnicalIssueViews(this.analysisData).warnings;
  }

  hasTechnicalCriticalIssues(): boolean {
    return this.technicalCriticalIssueViews().length > 0;
  }

  hasTechnicalWarnings(): boolean {
    return this.technicalWarningIssueViews().length > 0;
  }

  isTechnicalIssueDetailsExpanded(kind: 'critical' | 'warning', index: number): boolean {
    return this.technicalIssueDetailsExpanded.has(`${kind}:${index}`);
  }

  toggleTechnicalIssueDetails(kind: 'critical' | 'warning', index: number): void {
    const key = `${kind}:${index}`;
    if (this.technicalIssueDetailsExpanded.has(key)) {
      this.technicalIssueDetailsExpanded.delete(key);
    } else {
      this.technicalIssueDetailsExpanded.add(key);
    }
  }

  formatTechnicalPerfMs(value: unknown): string {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return `${Math.round(Number(value))} ms`;
  }

  toggleContentSection(): void {
    this.contentSectionCollapsed = !this.contentSectionCollapsed;
  }

  toggleLinksSection(): void {
    this.linksSectionCollapsed = !this.linksSectionCollapsed;
  }

  /**
   * Extract internal links data from analysis
   */
  extractLinkData(): void {
    if (!this.analysisData?.links?.internalLinks) {
      this.internalLinks = [];
      return;
    }

    this.internalLinks = this.analysisData.links.internalLinks || [];
  }

  /**
   * Get filtered and sorted links
   */
  getFilteredLinks(): any[] {
    let filtered = this.internalLinks;

    // Apply filter
    if (this.linkFilter !== 'all') {
      filtered = filtered.filter(link => link.linkType === this.linkFilter);
    }

    // Apply sort
    filtered = [...filtered].sort((a, b) => {
      switch (this.linkSort) {
        case 'type':
          const typeOrder = { 'structural': 1, 'contextual': 2, 'same_page': 3, 'unknown': 4 };
          return (typeOrder[a.linkType as keyof typeof typeOrder] || 99) - 
                 (typeOrder[b.linkType as keyof typeof typeOrder] || 99);
        case 'confidence':
          return (b.confidence || 0) - (a.confidence || 0);
        case 'anchor':
          return (a.anchorText || '').localeCompare(b.anchorText || '');
        case 'url':
          return (a.targetRelativeUrl || '').localeCompare(b.targetRelativeUrl || '');
        default:
          return 0;
      }
    });

    return filtered;
  }

  /**
   * Get link statistics
   */
  getLinkStats(): { total: number; structural: number; contextual: number; samePage: number; unknown: number } {
    return {
      total: this.internalLinks.length,
      structural: this.internalLinks.filter(l => l.linkType === 'structural').length,
      contextual: this.internalLinks.filter(l => l.linkType === 'contextual').length,
      samePage: this.internalLinks.filter(l => l.linkType === 'same_page').length,
      unknown: this.internalLinks.filter(l => l.linkType === 'unknown' || !l.linkType).length
    };
  }

  /**
   * Get link type badge class
   */
  getLinkTypeClass(linkType: string): string {
    switch (linkType) {
      case 'structural': return 'link-badge-structural';
      case 'contextual': return 'link-badge-contextual';
      case 'same_page': return 'link-badge-same-page';
      default: return 'link-badge-unknown';
    }
  }

  /**
   * Get link type label
   */
  getLinkTypeLabel(linkType: string): string {
    switch (linkType) {
      case 'structural': return 'Structural';
      case 'contextual': return 'Contextual';
      case 'same_page': return 'Same Page';
      default: return 'Unknown';
    }
  }

  /**
   * Get confidence color class
   */
  getConfidenceClass(confidence: number): string {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.6) return 'confidence-medium';
    return 'confidence-low';
  }

  /**
   * Check if links are available
   */
  hasLinks(): boolean {
    return this.internalLinks.length > 0;
  }

  /**
   * Toggle link details expansion
   */
  toggleLinkDetails(link: any): void {
    link._expanded = !link._expanded;
  }

  /**
   * Get the structured content from the analysis data
   */
  getStructuredContent(): any {
    if (!this.analysisData?.rawData?.structuredContent) {
      return null;
    }
    return this.analysisData.rawData.structuredContent;
  }

  // TODO: DataForSEO markdown_view → page_as_markdown (see CONTENT_INVENTORY_TOPIC_COMPARE_UI.md)
  // This would provide clean markdown content instead of structured content
  // Consider implementing this in the future for better content display

  /**
   * Get heading level class for styling
   */
  getHeadingClass(level: number): string {
    return `heading-level-${level}`;
  }

  /**
   * Check if content has any elements
   */
  hasContent(): boolean {
    return this.contentElements.length > 0;
  }

  /**
   * TrackBy function for ngFor performance
   */
  trackByElement(index: number, element: ContentElement): string {
    return `${element.type}-${index}-${element.content.substring(0, 20)}`;
  }

  /**
   * TrackBy function for links
   */
  trackByLink(index: number, link: any): string {
    return `${link.targetRelativeUrl}-${link.anchorText}-${index}`;
  }

  hasTopicSignals(): boolean {
    const raw = this.analysisData?.topic?.topicSignals;
    return Array.isArray(raw) && raw.length > 0;
  }

  /**
   * Topic signals for this page, strongest first (same ordering as useful for scanning).
   */
  private static readonly TOP_PAGE_TOPIC_SIGNALS = 5;

  getPageTopicSignals(): PageTopicSignal[] {
    const raw = this.analysisData?.topic?.topicSignals;
    if (!Array.isArray(raw)) return [];
    const mapped = raw
      .filter((s: any) => s && typeof s.topicSlug === 'string')
      .map((s: any) => ({
        topicSlug: s.topicSlug,
        label: typeof s.label === 'string' && s.label.trim() ? s.label : s.topicSlug,
        weight: Number.isFinite(s.weight) ? s.weight : 0,
        salience: Number.isFinite(s.salience) ? s.salience : 0,
        relativeDensity: Number.isFinite(s.relativeDensity) ? s.relativeDensity : 0
      }));
    return mapped.sort((a, b) => b.weight - a.weight);
  }

  getTopPageTopicSignals(): PageTopicSignal[] {
    return this.getPageTopicSignals().slice(0, UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS);
  }

  hasMorePageTopicsThanTop(): boolean {
    return this.getPageTopicSignals().length > UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS;
  }

  togglePageTopicsExpanded(): void {
    this.pageTopicsExpanded = !this.pageTopicsExpanded;
  }

  pageTopicsExpandControlLabel(): string {
    if (this.pageTopicsExpanded) return 'Show top topics only';
    const total = this.getPageTopicSignals().length;
    const rest = total - UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS;
    return rest > 0 ? `Show more topics (${rest} more)` : 'Show all topics';
  }

  /**
   * Same blend as content inventory / domain topic intent: 0.7·weight + 0.2·salience + 0.1·relativeDensity (clamped 0..1).
   */
  pageTopicRelevance01(signal: PageTopicSignal): number {
    const v =
      0.7 * (signal.weight ?? 0) +
      0.2 * (signal.salience ?? 0) +
      0.1 * (signal.relativeDensity ?? 0);
    return Math.min(1, Math.max(0, v));
  }

  formatTopicMetric(n: number): string {
    return Number.isFinite(n) ? n.toFixed(3) : '—';
  }

  formatPageTopicRelevancePct(signal: PageTopicSignal): string {
    return `${Math.round(this.pageTopicRelevance01(signal) * 100)}%`;
  }

  pageTopicRelevanceScore0to100(signal: PageTopicSignal): number {
    return this.pageTopicRelevance01(signal) * 100;
  }

  trackByTopicSignal(index: number, signal: PageTopicSignal): string {
    return signal.topicSlug + '-' + index;
  }

  /** Placeholder columns/lines for supporting-pages skeleton */
  readonly topicOwnershipSkeletonCols = [0, 1, 2];
  readonly topicOwnershipSkeletonLines = [0, 1, 2, 3, 4];

  loadTopicOwnership(): void {
    const gen = ++this.topicOwnershipLoadGen;
    this.topicOwnershipCompareUrl = '';
    this.resetTopicOwnershipV2Test();
    this.supportingPageLexicalByUrl = new Map();
    this.topicOwnershipCacheMiss = false;
    this.topicOwnershipSnapshotGeneratedAt = null;
    const pageUrl = (this.url || '').trim();
    const yourDomain = this.extractDomainFromUrl(pageUrl);
    const yourDomainKey = yourDomain ? yourDomain.toLowerCase().replace(/^www\./, '') : '';
    if (!yourDomainKey || !this.analysisData) {
      this.topicOwnershipStatus = 'ready';
      this.topicOwnershipDomains = [];
      return;
    }

    const pageTopics = getTopTopicsForSupportingPages(this.analysisData, { limit: 3 });
    if (pageTopics.length === 0) {
      this.topicOwnershipStatus = 'ready';
      this.topicOwnershipDomains = [];
      this.focalAnchorTopicSlugs = new Set();
      this.focalAnchorTopicLabels = [];
      return;
    }

    const isComparisonLike = isComparisonLikeUrlForTopicAnchors(pageUrl);
    const anchor = getFocalAnchorTopicSlugs(
      this.analysisData,
      isComparisonLike ? { minRelativeWeight: FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT } : undefined
    );
    this.focalAnchorTopicSlugs = anchor.slugs;
    this.focalAnchorTopicLabels = anchor.labels;

    this.topicOwnershipStatus = 'loading';
    this.topicOwnershipDomains = [];
    this.topicOwnershipPagesExpandedByDomain.clear();
    this.topicOwnershipUserExpanded = false;

    this.dataForSEOService.getSupportingPagesByTopicSnapshot(pageUrl).subscribe({
      next: (res) => {
        if (gen !== this.topicOwnershipLoadGen) return;
        if (res.cached && res.data) {
          this.applySupportingPagesSnapshot(res.data);
          this.topicOwnershipStatus = 'ready';
          this.loadTopicOwnershipV2FromCache();
          return;
        }
        this.topicOwnershipDomains = [];
        this.topicOwnershipCacheMiss = true;
        this.topicOwnershipStatus = 'ready';
        this.topicOwnershipV2Status = 'ready';
      },
      error: () => {
        if (gen !== this.topicOwnershipLoadGen) return;
        this.topicOwnershipStatus = 'error';
        this.topicOwnershipDomains = [];
        this.topicOwnershipCacheMiss = true;
      }
    });
  }

  private applySupportingPagesSnapshot(data: SupportingPagesByTopicSnapshotData): void {
    this.focalAnchorTopicSlugs = new Set(data.focalAnchorTopicSlugs || []);
    this.focalAnchorTopicLabels = data.focalAnchorTopicLabels || [];
    this.topicOwnershipSnapshotGeneratedAt = data.generatedAt || null;
    this.topicOwnershipDomains = (data.domains || [])
      .filter((block) => block.isYourSite || (block.rows?.length ?? 0) > 0)
      .map((block) => ({
      domain: block.domain,
      isYourSite: block.isYourSite,
      rows: block.rows as DomainTopicUrlRow[]
    }));
    this.topicOwnershipCacheMiss = false;
  }

  private loadTopicOwnershipV2FromCache(): void {
    const focalUrl = (this.url || '').trim();
    if (!focalUrl || this.topicOwnershipStatus !== 'ready') return;

    const gen = ++this.topicOwnershipV2LoadGen;
    this.topicOwnershipV2Status = 'loading';
    this.topicOwnershipV2Error = null;
    this.topicOwnershipV2Data = null;

    this.dataForSEOService.getSupportingPagesOpportunitySnapshot(focalUrl).subscribe({
      next: (res) => {
        if (gen !== this.topicOwnershipV2LoadGen) return;
        if (res.cached && res.data) {
          this.topicOwnershipV2Data = res.data;
          this.topicOwnershipV2Status = 'ready';
          return;
        }
        this.topicOwnershipV2Status = 'ready';
      },
      error: (err) => {
        if (gen !== this.topicOwnershipV2LoadGen) return;
        this.topicOwnershipV2Status = 'error';
        this.topicOwnershipV2Error =
          err?.error?.message || err?.message || 'Supporting pages opportunity snapshot failed';
      }
    });
  }

  devRebuildSupportingPagesByTopic(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.supportingPagesV1RebuildRunning) return;

    this.supportingPagesV1RebuildRunning = true;
    this.supportingPagesRebuildStatus = 'Building Supporting pages by topic…';
    this.resetTopicOwnershipV2Test();

    this.dataForSEOService.rebuildSupportingPagesByTopicSnapshot(pageUrl).subscribe({
      next: (res) => {
        this.supportingPagesV1RebuildRunning = false;
        this.supportingPagesRebuildStatus = res.message || 'Supporting pages snapshot rebuilt';
        if (res.data) {
          this.applySupportingPagesSnapshot(res.data);
          this.topicOwnershipStatus = 'ready';
          this.loadTopicOwnershipV2FromCache();
        } else {
          this.topicOwnershipDomains = [];
          this.topicOwnershipCacheMiss = true;
          this.topicOwnershipStatus = 'ready';
          this.topicOwnershipV2Status = 'ready';
        }
      },
      error: (err) => {
        this.supportingPagesV1RebuildRunning = false;
        this.supportingPagesRebuildStatus =
          err?.error?.message || err?.message || 'Supporting pages rebuild failed';
      }
    });
  }

  devRebuildSupportingPagesOpportunities(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.supportingPagesOppRebuildRunning) return;

    this.supportingPagesOppRebuildRunning = true;
    this.supportingPagesRebuildStatus = 'Building Supporting page opportunities…';

    this.dataForSEOService
      .rebuildSupportingPagesOpportunitySnapshot(pageUrl, { skipV1: true })
      .subscribe({
        next: (res) => {
          this.supportingPagesOppRebuildRunning = false;
          this.supportingPagesRebuildStatus = res.message || 'Opportunity snapshot rebuilt';
          if (res.data?.opportunities) {
            this.topicOwnershipV2Data = res.data.opportunities;
            this.topicOwnershipV2Status = 'ready';
            this.topicOwnershipV2Error = null;
          } else {
            this.topicOwnershipV2Data = null;
            this.topicOwnershipV2Status = 'ready';
          }
        },
        error: (err) => {
          this.supportingPagesOppRebuildRunning = false;
          this.supportingPagesRebuildStatus =
            err?.error?.message || err?.message || 'Opportunity rebuild failed';
          this.topicOwnershipV2Status = 'error';
          this.topicOwnershipV2Error =
            err?.error?.message || err?.message || 'Opportunity rebuild failed';
        }
      });
  }

  private resetTopicOwnershipV2Test(): void {
    this.topicOwnershipV2LoadGen++;
    this.topicOwnershipV2Status = 'idle';
    this.topicOwnershipV2Data = null;
    this.topicOwnershipV2Error = null;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2TextUrl = '';
    this.topicOwnershipV2CalUrl = '';
    this.topicOwnershipV2SectionCompareCollapsedKeys.clear();
  }

  private findTopicOwnershipRow(pageUrl: string): DomainTopicUrlRow | null {
    const key = normalizeUrlForCompare(pageUrl);
    for (const block of this.topicOwnershipDomains) {
      for (const row of block.rows) {
        if (normalizeUrlForCompare(row.url) === key) return row;
      }
    }
    return null;
  }

  private loadTopicOwnershipV2Test(): void {
    this.loadTopicOwnershipV2FromCache();
  }

  retryTopicOwnershipV2Test(): void {
    if (this.isDevView()) {
      this.devRebuildSupportingPagesOpportunities();
      return;
    }
    this.loadTopicOwnershipV2FromCache();
  }

  toggleTopicOwnershipV2Detail(competitorUrl: string): void {
    if (this.topicOwnershipV2DetailUrl === competitorUrl) {
      this.topicOwnershipV2DetailUrl = '';
      return;
    }
    this.topicOwnershipV2DetailUrl = competitorUrl;
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2TextUrl = '';
    this.topicOwnershipV2CalUrl = '';
  }

  toggleTopicOwnershipV2Text(competitorUrl: string): void {
    if (this.topicOwnershipV2TextUrl === competitorUrl) {
      this.topicOwnershipV2TextUrl = '';
      return;
    }
    this.topicOwnershipV2TextUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2CalUrl = '';
  }

  toggleTopicOwnershipV2Cal(competitorUrl: string): void {
    if (this.topicOwnershipV2CalUrl === competitorUrl) {
      this.topicOwnershipV2CalUrl = '';
      return;
    }
    this.topicOwnershipV2CalUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2TextUrl = '';
  }

  topicOwnershipV2NovelClass(level: string): string {
    if (level === 'strong') return 'url-topic-ownership-v2-pill--strong';
    if (level === 'depth') return 'url-topic-ownership-v2-pill--depth';
    if (level === 'aligned') return 'url-topic-ownership-v2-pill--aligned';
    return 'url-topic-ownership-v2-pill--unknown';
  }

  topicOwnershipV2ActionClass(action: string): string {
    if (action === 'expand_focal') return 'url-topic-ownership-v2-action--expand';
    if (action === 'new_supporting_page') return 'url-topic-ownership-v2-action--new';
    if (action === 'covered_elsewhere') return 'url-topic-ownership-v2-action--covered';
    return 'url-topic-ownership-v2-action--muted';
  }

  topicOwnershipV2RowHighlight(row: { opportunityPriority: number; novelVsFocal: string }): boolean {
    return row.opportunityPriority >= 50 || row.novelVsFocal === 'strong';
  }

  topicOwnershipV2RowPath(pageUrl: string): string {
    return this.topicOwnershipPagePath(pageUrl);
  }

  topicOwnershipV2RowDomain(pageUrl: string): string {
    try {
      return new URL(pageUrl).hostname.replace(/^www\./i, '');
    } catch {
      return pageUrl;
    }
  }

  topicOwnershipV2RowDomainPath(pageUrl: string): string {
    return `${this.topicOwnershipV2RowDomain(pageUrl)}${this.topicOwnershipV2RowPath(pageUrl)}`;
  }

  /** Position in the focal page's target-query SERP snapshot (1–10), or null if URL absent from top 10. */
  topicOwnershipV2SerpPositionForUrl(pageUrl: string): number | null {
    if (!this.pageSerp?.results?.length) return null;
    const targetKey = normalizeUrlForCompare(pageUrl);
    if (!targetKey) return null;
    for (const row of this.pageSerp.results) {
      if (normalizeUrlForCompare(row.url) === targetKey) {
        return row.position;
      }
    }
    return null;
  }

  topicOwnershipV2ShowSerpRankChip(pageUrl: string): boolean {
    return this.hasPageSerpSnapshot();
  }

  topicOwnershipV2SerpRankChipLabel(pageUrl: string): string {
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    if (pos != null && pos >= 1) return `Ranks #${pos}`;
    return 'Not in top 10';
  }

  topicOwnershipV2SerpRankChipTitle(pageUrl: string): string {
    const query = this.pageSerpTargetQueryLabel();
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    const page = this.topicOwnershipV2RowDomainPath(pageUrl);
    if (pos != null) {
      return `${page} ranks #${pos} for «${query}» in this URL's top-10 SERP snapshot.`;
    }
    return `${page} is not in the top 10 for «${query}» in this URL's SERP snapshot.`;
  }

  topicOwnershipV2SerpRankChipClass(pageUrl: string): string {
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    if (pos == null) return 'url-topic-ownership-v2-serp-chip--absent';
    if (pos <= 3) return 'url-topic-ownership-v2-serp-chip--strong';
    return 'url-topic-ownership-v2-serp-chip--ranked';
  }

  topicOwnershipV2HasSectionCoverage(row: SupportingPagesV2RowResult): boolean {
    return this.topicOwnershipV2SectionCoverageRows(row).length > 0;
  }

  topicOwnershipV2SectionCoverageRows(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    const rows = row.sectionCoverage?.length ? [...row.sectionCoverage] : [];
    return rows.sort((a, b) => (a.sectionNumber ?? 0) - (b.sectionNumber ?? 0));
  }

  topicOwnershipV2SectionCoverageGroups(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    let groups = row.sectionCoverageGroups?.length ? [...row.sectionCoverageGroups] : [];
    if (groups.length) {
      if (!this.isDevView()) {
        groups = groups.filter((sc) => !sc.listicleSuppressed);
      }
      return groups.sort((a, b) => (a.sectionNumber ?? 0) - (b.sectionNumber ?? 0));
    }
    return this.topicOwnershipV2SectionCoverageRows(row).filter(
      (sc) => this.topicOwnershipV2SectionNotOnPage(sc)
    );
  }

  topicOwnershipV2CompareFilterMode(): 'not_on_page' | 'gaps' | 'all' {
    return this.topicOwnershipV2CompareFilter;
  }

  setTopicOwnershipV2CompareFilter(mode: 'not_on_page' | 'gaps' | 'all'): void {
    this.topicOwnershipV2CompareFilter = mode;
  }

  /** Section missing entirely on focal page. */
  topicOwnershipV2SectionNotOnPage(sc: SectionCoverageRow): boolean {
    return !sc.sectionCovered;
  }

  /** A section is a gap when its topic is uncovered or it has absent phrases. */
  topicOwnershipV2SectionIsGap(sc: SectionCoverageRow): boolean {
    return !sc.sectionCovered || (sc.absentTerms?.length ?? 0) > 0;
  }

  topicOwnershipV2FilteredSectionCoverageRows(
    row: SupportingPagesV2RowResult
  ): SectionCoverageRow[] {
    const mode = this.topicOwnershipV2CompareFilterMode();
    if (!this.isDevView() && mode === 'not_on_page') {
      return this.topicOwnershipV2SectionCoverageGroups(row);
    }
    const rows = this.topicOwnershipV2SectionCoverageRows(row);
    if (mode === 'all') return rows;
    if (mode === 'not_on_page') return rows.filter((sc) => this.topicOwnershipV2SectionNotOnPage(sc));
    return rows.filter((sc) => this.topicOwnershipV2SectionIsGap(sc));
  }

  topicOwnershipV2CompareFilterEmptyMessage(): string {
    const mode = this.topicOwnershipV2CompareFilterMode();
    if (mode === 'not_on_page') {
      return 'Every competitor section is covered somewhere on your page — switch filter to review gaps or all sections.';
    }
    if (mode === 'gaps') {
      return 'No section gaps — switch to All sections to review full coverage.';
    }
    return '';
  }

  topicOwnershipV2SectionPositionChip(sc: SectionCoverageRow): string {
    const num = sc.sectionNumber;
    const end = sc.sectionNumberEnd;
    const total = sc.sectionTotal;
    const place = (sc.pagePosition || '').trim();
    if (num != null && total != null && total > 0) {
      const ordinal =
        end != null && end > num ? `${num}–${end}/${total}` : `${num}/${total}`;
      return place ? `${ordinal} · ${place}` : ordinal;
    }
    return place || '';
  }

  topicOwnershipV2ListicleSectionStatusLabel(sc: SectionCoverageRow): string {
    const tool = (sc.listicleToolLabel || 'tool').trim();
    if (sc.listicleSectionRole === 'tool_root') {
      return sc.listicleToolOnFocal ? `Listicle tool — ${tool}` : 'Listicle tool — not on your list';
    }
    if (sc.listicleSectionRole === 'tool_feature') {
      return sc.listicleToolOnFocal
        ? `Listicle feature — ${tool}`
        : `Listicle feature — ${tool} (use list inventory)`;
    }
    return 'Listicle section';
  }

  topicOwnershipV2ListicleSectionStatusTitle(sc: SectionCoverageRow): string {
    const tool = (sc.listicleToolLabel || 'tool').trim();
    if (sc.listicleSectionRole === 'tool_root' && !sc.listicleToolOnFocal) {
      return `Section is a numbered tool slot (${tool}) missing from your roundup — use list inventory, not section compare.`;
    }
    if (sc.listicleSectionRole === 'tool_feature' && !sc.listicleToolOnFocal) {
      return `Feature under ${tool}, which is not on your list — use list inventory for the tool gap.`;
    }
    if (sc.listicleSectionRole === 'tool_feature' && sc.listicleToolOnFocal) {
      return `Feature subsection for ${tool} — both pages list this tool; section compare is valid here.`;
    }
    if (sc.listicleSectionRole === 'tool_root' && sc.listicleToolOnFocal) {
      return `Numbered tool slot for ${tool} — on both roundups.`;
    }
    return 'Listicle-structured section.';
  }

  topicOwnershipV2SectionStatusTitle(sc: SectionCoverageRow): string | null {
    if (sc.isNonTopical) return this.topicOwnershipV2NonTopicalStatusTitle(sc);
    if (this.isDevView() && sc.listicleSectionRole) {
      return this.topicOwnershipV2ListicleSectionStatusTitle(sc);
    }
    if (sc.listicleSuppressed) {
      return 'Gap group suppressed in user view — tool not on your roundup (see list inventory).';
    }
    return null;
  }

  topicOwnershipV2SectionStatusLabel(sc: SectionCoverageRow): string {
    if (sc.isNonTopical) return this.topicOwnershipV2NonTopicalStatusLabel(sc);
    if (this.isDevView() && sc.listicleSectionRole) {
      return this.topicOwnershipV2ListicleSectionStatusLabel(sc);
    }
    if (sc.listicleSuppressed && this.isDevView()) return 'Listicle gap (user hidden)';
    if (!sc.sectionCovered) return 'Not on your page';
    if ((sc.absentTerms?.length ?? 0) > 0) return 'Covered — missing phrases';
    return 'Covered';
  }

  topicOwnershipV2NonTopicalStatusLabel(sc: SectionCoverageRow): string {
    if (sc.nonTopicalReason === 'author_bio') return 'Author bio (ignored)';
    if (sc.nonTopicalReason === 'newsletter_signup') return 'Newsletter signup (ignored)';
    if (sc.nonTopicalReason === 'generic_heading') return 'Non-article section (ignored)';
    return 'Non-article section (ignored)';
  }

  topicOwnershipV2NonTopicalStatusTitle(sc: SectionCoverageRow): string {
    if (sc.nonTopicalReason === 'author_bio') {
      return 'Detected as an author/byline block — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'newsletter_signup') {
      return 'Detected as a newsletter / email signup CTA — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'generic_heading') {
      return 'Structural section (intro, conclusion, FAQ, etc.) — not scored as a content gap.';
    }
    return 'Excluded from gap scoring — not part of the article body comparison.';
  }

  topicOwnershipV2SectionStatusClass(sc: SectionCoverageRow): string {
    if (sc.isNonTopical) return 'url-topic-ownership-v2-section-status--non-topical';
    if (
      this.isDevView() &&
      sc.listicleSectionRole &&
      !sc.listicleToolOnFocal
    ) {
      return 'url-topic-ownership-v2-section-status--listicle';
    }
    if (sc.listicleSuppressed && this.isDevView()) {
      return 'url-topic-ownership-v2-section-status--listicle';
    }
    if (!sc.sectionCovered) return 'url-topic-ownership-v2-section-status--missing';
    if ((sc.absentTerms?.length ?? 0) > 0) return 'url-topic-ownership-v2-section-status--shallow';
    return 'url-topic-ownership-v2-section-status--aligned';
  }

  topicOwnershipV2SectionBodyText(sc: SectionCoverageRow): string {
    return (sc.competitorText || '').trim();
  }

  topicOwnershipV2SectionHeadingPath(sc: SectionCoverageRow): string {
    return (sc.competitorHeadingPath || sc.competitorHeading || '').trim();
  }

  private headingPathSegmentsFromLegacyPath(
    path: string,
    leafLevel?: number | null
  ): HeadingPathSegment[] {
    const parts = path
      .split('>')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return [];
    return parts.map((heading, index) => ({
      heading,
      level: index === parts.length - 1 ? Math.round(Number(leafLevel) || 0) : 0
    }));
  }

  private mergeHeadingPathSegments(
    path: string,
    apiSegments: HeadingPathSegment[] | undefined | null,
    leafLevel?: number | null
  ): HeadingPathSegment[] {
    const parts = path
      .split('>')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) {
      return apiSegments?.length ? apiSegments : [];
    }

    const api = apiSegments || [];
    if (api.length === parts.length) {
      return parts.map((heading, index) => ({
        heading,
        level:
          api[index]?.level ??
          (index === parts.length - 1 ? Math.round(Number(leafLevel) || 0) : 0)
      }));
    }

    if (parts.length > api.length) {
      const leaf =
        api[api.length - 1]?.level ??
        Math.round(Number(leafLevel) || 0) ??
        0;
      return parts.map((heading, index) => {
        const matched = api.find((seg) => seg.heading === heading);
        if (matched) return matched;
        const depthFromLeaf = parts.length - 1 - index;
        const inferred = leaf > 0 ? Math.max(2, leaf - depthFromLeaf) : 0;
        return { heading, level: inferred };
      });
    }

    if (api.length) return api;
    return this.headingPathSegmentsFromLegacyPath(path, leafLevel);
  }

  topicOwnershipV2SectionHeadingSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    return this.mergeHeadingPathSegments(
      this.topicOwnershipV2SectionHeadingPath(sc),
      sc.competitorHeadingPathSegments,
      sc.competitorHeadingLevel
    );
  }

  private topicOwnershipV2HeadingSegmentKey(seg: HeadingPathSegment): string {
    return `${seg.level}::${(seg.heading || '').trim()}`;
  }

  topicOwnershipV2SectionDisplayHeadingSegments(
    sc: SectionCoverageRow
  ): Array<HeadingPathSegment & { displayRole?: string; treeDepth?: number }> {
    const memberPaths = (sc.gapMemberPaths || [])
      .map((member) => member.headingPathSegments || [])
      .filter((path) => path.length > 0);
    if (sc.isGapGroup && memberPaths.length) {
      const gapLeafKeys = new Set(
        memberPaths.map((path) => this.topicOwnershipV2HeadingSegmentKey(path[path.length - 1]))
      );
      const leafLevel = memberPaths[0][memberPaths[0].length - 1]?.level;
      const sameLeafLevel = memberPaths.every(
        (path) => path[path.length - 1]?.level === leafLevel
      );
      const parentKey = memberPaths[0]
        .slice(0, -1)
        .map((seg) => seg.heading)
        .join(' > ');
      const sameParent = memberPaths.every(
        (path) => path.slice(0, -1).map((seg) => seg.heading).join(' > ') === parentKey
      );

      if (sameLeafLevel && sameParent && memberPaths.length > 1) {
        const ancestors = memberPaths[0].slice(0, -1).map((seg, index) => ({
          ...seg,
          displayRole: 'ancestor',
          treeDepth: index
        }));
        const seen = new Set<string>();
        const leaves = memberPaths
          .map((path) => path[path.length - 1])
          .filter((leaf) => {
            const key = this.topicOwnershipV2HeadingSegmentKey(leaf);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map((leaf) => ({
            ...leaf,
            displayRole: 'gap',
            treeDepth: ancestors.length
          }));
        return [...ancestors, ...leaves];
      }

      const spine = memberPaths.reduce(
        (longest, path) => (path.length > longest.length ? path : longest),
        memberPaths[0]
      );
      const rows: Array<HeadingPathSegment & { displayRole?: string; treeDepth?: number }> = [];
      const seenGap = new Set<string>();
      spine.forEach((seg, depth) => {
        const key = this.topicOwnershipV2HeadingSegmentKey(seg);
        rows.push({
          ...seg,
          displayRole: gapLeafKeys.has(key) ? 'gap' : 'ancestor',
          treeDepth: depth
        });
        if (gapLeafKeys.has(key)) seenGap.add(key);
      });
      for (const path of memberPaths) {
        const leaf = path[path.length - 1];
        const key = this.topicOwnershipV2HeadingSegmentKey(leaf);
        if (!seenGap.has(key)) {
          rows.push({
            ...leaf,
            displayRole: 'gap',
            treeDepth: path.length - 1
          });
          seenGap.add(key);
        }
      }
      return rows;
    }

    const segments = this.topicOwnershipV2SectionHeadingSegments(sc);
    if (!sc.sectionCovered && !sc.isNonTopical && segments.length) {
      return segments.map((seg, index) => ({
        ...seg,
        displayRole: index === segments.length - 1 ? 'gap' : 'ancestor',
        treeDepth: index
      }));
    }
    return segments.map((seg, index) => ({ ...seg, displayRole: 'default', treeDepth: index }));
  }

  topicOwnershipV2SectionDisplayHeadingRole(seg: {
    displayRole?: string;
  }): string {
    const role = (seg.displayRole || 'default').trim();
    if (role === 'gap') return 'url-topic-ownership-v2-heading-path-segment--gap';
    if (role === 'covered-sibling') {
      return 'url-topic-ownership-v2-heading-path-segment--covered-sibling';
    }
    return '';
  }

  topicOwnershipV2ClosestHeadingSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) return [];
    return this.mergeHeadingPathSegments(
      this.topicOwnershipV2ClosestHeadingPath(sc),
      sc.closestMyHeadingPathSegments,
      sc.closestMyHeadingLevel
    );
  }

  topicOwnershipV2HeadingLevelChip(level?: number | null): string | null {
    const n = Math.round(Number(level) || 0);
    if (n >= 1 && n <= 6) return `H${n}`;
    return null;
  }

  topicOwnershipV2SectionHeadingLevelChip(sc: SectionCoverageRow): string | null {
    return this.topicOwnershipV2HeadingLevelChip(sc.competitorHeadingLevel);
  }

  topicOwnershipV2ClosestHeadingLevelChip(sc: SectionCoverageRow): string | null {
    return this.topicOwnershipV2HeadingLevelChip(sc.closestMyHeadingLevel);
  }

  topicOwnershipV2FocalDomain(): string {
    return this.topicOwnershipV2RowDomain(this.url || '');
  }

  topicOwnershipV2ClosestHeadingPath(sc: SectionCoverageRow): string {
    return (sc.closestMyHeadingPath || sc.closestMyHeading || '').trim() || '(Untitled)';
  }

  topicOwnershipV2SectionHighlightSegments(
    sc: SectionCoverageRow
  ): SectionCompareHighlightSegment[] {
    return splitSectionTextByAbsentTerms(
      this.topicOwnershipV2SectionBodyText(sc),
      sc.absentTerms || []
    );
  }

  topicOwnershipV2HasClosestMatch(sc: SectionCoverageRow): boolean {
    return !!(sc.closestMyHeading?.trim() || sc.closestMyText?.trim());
  }

  topicOwnershipV2SectionCompareAvailable(sc: SectionCoverageRow): boolean {
    if (sc.isNonTopical) return false;
    return (
      !!this.topicOwnershipV2SectionBodyText(sc) ||
      !!this.topicOwnershipV2ClosestMyBodyText(sc) ||
      !!sc.closestMyHeading?.trim()
    );
  }

  topicOwnershipV2ClosestMatchPct(sc: SectionCoverageRow): number {
    return Math.round((sc.sectionSimilarity || 0) * 100);
  }

  topicOwnershipV2ClosestMatchTitle(sc: SectionCoverageRow): string {
    const pct = this.topicOwnershipV2ClosestMatchPct(sc);
    return (
      `Embedding similarity ${pct}% between this competitor section and the closest block on your focal page. ` +
      `Sections need ≥55% to count as covered. Higher scores mean a closer match, not guaranteed topical coverage — read the text to verify.`
    );
  }

  topicOwnershipV2ClosestMyBodyText(sc: SectionCoverageRow): string {
    return (sc.closestMyText || '').trim();
  }

  topicOwnershipV2CompareClosestLabel(sc: SectionCoverageRow): string | null {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) return null;
    if (sc.sectionCovered) return null;
    return `Closest on your page (${this.topicOwnershipV2ClosestMatchPct(sc)}% similar)`;
  }

  topicOwnershipV2CompareRightColumnTitle(sc: SectionCoverageRow): string {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) {
      return 'No matching section';
    }
    return this.topicOwnershipV2ClosestHeadingPath(sc);
  }

  topicOwnershipV2CompareRightColumnSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) {
      return [{ level: 0, heading: 'No matching section' }];
    }
    return this.topicOwnershipV2ClosestHeadingSegments(sc);
  }

  topicOwnershipV2SectionFullTextKey(competitorUrl: string, sc: SectionCoverageRow): string {
    if (sc.isGapGroup) {
      const end = sc.sectionNumberEnd ?? sc.sectionNumber ?? 0;
      return `${competitorUrl}::group::${sc.sectionNumber ?? 0}::${end}`;
    }
    return `${competitorUrl}::${sc.sectionNumber ?? 0}`;
  }

  isTopicOwnershipV2SectionFullTextExpanded(competitorUrl: string, sc: SectionCoverageRow): boolean {
    if (!this.topicOwnershipV2SectionCompareAvailable(sc)) return false;
    return !this.topicOwnershipV2SectionCompareCollapsedKeys.has(
      this.topicOwnershipV2SectionFullTextKey(competitorUrl, sc)
    );
  }

  toggleTopicOwnershipV2SectionFullText(competitorUrl: string, sc: SectionCoverageRow): void {
    const key = this.topicOwnershipV2SectionFullTextKey(competitorUrl, sc);
    if (this.topicOwnershipV2SectionCompareCollapsedKeys.has(key)) {
      this.topicOwnershipV2SectionCompareCollapsedKeys.delete(key);
    } else {
      this.topicOwnershipV2SectionCompareCollapsedKeys.add(key);
    }
  }

  topicOwnershipPagePath(pageUrl: string): string {
    return formatSupportingPagePath(pageUrl);
  }

  topicOwnershipPageTraffic(traffic: number | undefined): string {
    return formatSupportingPageTraffic(traffic);
  }

  getTopicOwnershipVisibleRows(block: TopicOwnershipDomain): DomainTopicUrlRow[] {
    const rows = this.getTopicOwnershipRowsForDisplay(block);
    if (this.useTopicOwnershipUserLayout()) {
      if (!block.isYourSite) return rows;
      if (this.topicOwnershipUserExpanded) return rows;
      return rows.slice(0, SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
    }
    if (this.isTopicOwnershipPagesExpanded(block.domain)) {
      return rows;
    }
    return rows.slice(0, SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT);
  }

  /** Rows tagged with focal anchor topics (hides weak secondary-topic pages from diagnostics). */
  private getTopicOwnershipRowsForDisplay(block: TopicOwnershipDomain): DomainTopicUrlRow[] {
    if (!this.focalAnchorTopicSlugs.size) {
      return block.rows;
    }
    return filterRowsByFocalAnchorTopics(block.rows, this.focalAnchorTopicSlugs);
  }

  getYourSiteMatchPoolRows(): DomainTopicUrlRow[] {
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    if (!yours?.rows.length) return [];
    if (!this.focalAnchorTopicSlugs.size) return yours.rows;
    return filterRowsByFocalAnchorTopics(yours.rows, this.focalAnchorTopicSlugs);
  }

  topicOwnershipAnchorHint(): string {
    if (!this.focalAnchorTopicLabels.length) return '';
    return this.focalAnchorTopicLabels.join(', ');
  }

  isTopicOwnershipRowOffAnchor(block: TopicOwnershipDomain, row: DomainTopicUrlRow): boolean {
    return (
      this.isDevView() &&
      block.isYourSite &&
      this.focalAnchorTopicSlugs.size > 0 &&
      !rowOverlapsFocalAnchorTopics(row, this.focalAnchorTopicSlugs)
    );
  }

  hasMoreTopicOwnershipPagesThanVisible(block: TopicOwnershipDomain): boolean {
    if (this.useTopicOwnershipUserLayout()) return false;
    return this.getTopicOwnershipRowsForDisplay(block).length > SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT;
  }

  hasMoreTopicOwnershipPagesOnYourSite(): boolean {
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    if (!yours) return false;
    return this.getTopicOwnershipRowsForDisplay(yours).length > SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT;
  }

  toggleTopicOwnershipUserExpanded(): void {
    this.topicOwnershipUserExpanded = !this.topicOwnershipUserExpanded;
  }

  topicOwnershipUserExpandLabel(): string {
    if (this.topicOwnershipUserExpanded) return 'Show fewer pages';
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    const hidden = Math.max(
      0,
      (yours?.rows.length ?? 0) - SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT
    );
    return hidden > 0 ? `Show more pages (${hidden} more)` : 'Show more pages';
  }

  topicOwnershipLinkTitle(block: TopicOwnershipDomain, row: DomainTopicUrlRow): string {
    if (block.isYourSite) return row.url;
    if (row.similarOnYourSite?.url) {
      return `${row.url}\nCovered — similar: ${row.similarOnYourSite.url}`;
    }
    return `${row.url}\nGap — no similar page on your site`;
  }

  isTopicOwnershipPagesExpanded(domain: string): boolean {
    return this.topicOwnershipPagesExpandedByDomain.has(domain.toLowerCase().trim());
  }

  toggleTopicOwnershipPagesExpanded(domain: string): void {
    const key = domain.toLowerCase().trim();
    if (this.topicOwnershipPagesExpandedByDomain.has(key)) {
      this.topicOwnershipPagesExpandedByDomain.delete(key);
    } else {
      this.topicOwnershipPagesExpandedByDomain.add(key);
    }
  }

  topicOwnershipPagesExpandLabel(block: TopicOwnershipDomain): string {
    if (this.isTopicOwnershipPagesExpanded(block.domain)) {
      return 'Show fewer pages';
    }
    const rest = block.rows.length - SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT;
    return rest > 0 ? `Show more pages (${rest} more)` : 'Show more pages';
  }

  isCurrentPageSupportingUrl(pageUrl: string): boolean {
    if (!this.url) return false;
    return normalizeUrlForCompare(pageUrl) === normalizeUrlForCompare(this.url);
  }

  private loadCompetitorTopicCoverage(): void {
    this.competitorCoverageStatus = 'idle';
    this.competitorCoverageMessage = '';
    this.competitorTopicMatchesBySlug = {};
    this.competitorTopicTooltipCache = {};

    const topicSignals = this.getPageTopicSignals();
    if (!topicSignals.length || !this.url) return;

    const activeDomain = this.extractDomainFromUrl(this.url);
    if (!activeDomain) return;

    this.competitorCoverageStatus = 'loading';

    this.compareContextService.getSettings(activeDomain).pipe(
      map((settingsRes) => {
        const domains = settingsRes?.data?.markedCompetitorDomains || [];
        this.markedCompetitorDomains = domains
          .map((d) => d.toLowerCase().trim().replace(/^www\./, ''))
          .filter((d) => !!d);
        return domains
          .map((d) => d.toLowerCase().trim())
          .filter((d) => !!d && d !== activeDomain);
      }),
      map((domains) => Array.from(new Set(domains))),
      map((domains) => domains.slice(0, 10)),
      catchError(() => {
        this.competitorCoverageStatus = 'error';
        this.competitorCoverageMessage = 'Could not load marked competitors for this domain.';
        return of([] as string[]);
      })
    ).subscribe((competitorDomains) => {
      if (this.competitorCoverageStatus === 'error') return;

      if (!competitorDomains.length) {
        this.competitorCoverageStatus = 'ready';
        this.competitorCoverageMessage = 'No marked competitors configured for this domain.';
        return;
      }

      const requests = (competitorDomains as string[]).map((domain: string) =>
        this.domainTopicsService.getDomainTopics(domain, { compact: true }).pipe(
          map((res) => ({ domain, topics: (res?.data?.parentTopics || []) as ParentTopic[] })),
          catchError(() => of({ domain, topics: [] as ParentTopic[] }))
        )
      );

      forkJoin(requests).subscribe({
        next: (competitorTopics: { domain: string; topics: ParentTopic[] }[]) => {
          this.competitorTopicMatchesBySlug = this.buildCompetitorMatches(topicSignals, competitorTopics);
          this.competitorCoverageStatus = 'ready';
          this.competitorCoverageMessage = '';
        },
        error: () => {
          this.competitorCoverageStatus = 'error';
          this.competitorCoverageMessage = 'Could not load competitor topic coverage.';
        }
      });
    });
  }

  private buildCompetitorMatches(
    signals: PageTopicSignal[],
    competitorTopics: Array<{ domain: string; topics: ParentTopic[] }>
  ): Record<string, CompetitorTopicMatch[]> {
    const bySlug: Record<string, CompetitorTopicMatch[]> = {};

    for (const signal of signals) {
      const normalizedTopicSlug = signal.topicSlug.toLowerCase().trim();
      const matches: CompetitorTopicMatch[] = [];

      for (const competitor of competitorTopics) {
        const topic = competitor.topics.find((t) => {
          const parentMatch = t.parentTopicSlug?.toLowerCase().trim() === normalizedTopicSlug;
          const childMatch = Array.isArray(t.childTopicSlugs)
            && t.childTopicSlugs.some((child) => child.toLowerCase().trim() === normalizedTopicSlug);
          return parentMatch || childMatch;
        });

        if (!topic) continue;

        matches.push({
          domain: competitor.domain,
          authorityScore: Number.isFinite(topic.authorityScore) ? topic.authorityScore : 0,
          pageCount: Number.isFinite(topic.pageCount) ? topic.pageCount : 0,
          matchedParentTopicSlug: topic.parentTopicSlug
        });
      }

      bySlug[signal.topicSlug] = matches.sort((a, b) => {
        if (b.authorityScore !== a.authorityScore) return b.authorityScore - a.authorityScore;
        if (b.pageCount !== a.pageCount) return b.pageCount - a.pageCount;
        return a.domain.localeCompare(b.domain);
      });
    }

    return bySlug;
  }

  extractDomainFromUrl(url: string): string {
    try {
      const parsed = new URL(url);
      return parsed.hostname.toLowerCase().replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  getCompetitorTopicMatches(signal: PageTopicSignal): CompetitorTopicMatch[] {
    return this.competitorTopicMatchesBySlug[signal.topicSlug] || [];
  }

  hasCompetitorMatches(signal: PageTopicSignal): boolean {
    return this.getCompetitorTopicMatches(signal).length > 0;
  }

  formatCompetitorPill(match: CompetitorTopicMatch): string {
    return `${match.domain} (${Math.round(match.authorityScore)} · ${match.pageCount})`;
  }

  private getCompetitorTopicTooltipKey(match: CompetitorTopicMatch): string {
    return `${match.domain}::${match.matchedParentTopicSlug}`;
  }

  onCompetitorPillHover(match: CompetitorTopicMatch): void {
    this.cancelCompetitorTooltipHide();
    const key = this.getCompetitorTopicTooltipKey(match);
    this.activeCompetitorTooltipKey = key;
    const cached = this.competitorTopicTooltipCache[key];
    if (cached && (cached.status === 'loading' || cached.status === 'ready' || cached.status === 'error')) {
      return;
    }

    this.competitorTopicTooltipCache[key] = { status: 'loading', urls: [] };

    this.domainTopicsService.getTopicDiagnostic(match.domain, match.matchedParentTopicSlug).pipe(
      map((res) => res?.data?.coverage?.directlyCoveredTopics || []),
      map((topics: any[]) => topics.find((t) => t?.parentTopicSlug === match.matchedParentTopicSlug)),
      map((topic: any) => {
        if (!topic) return [];
        const corePages = Array.isArray(topic.corePages) ? topic.corePages : [];
        const mentionPages = Array.isArray(topic.mentionPages) ? topic.mentionPages : [];
        const allPages = [...corePages, ...mentionPages];
        const byUrl = new Map<string, CompetitorTopicUrlPreview>();

        for (const page of allPages) {
          const pageUrl = typeof page?.url === 'string' ? page.url : '';
          if (!pageUrl) continue;
          const score = Number.isFinite(page?.seoIntentScore) ? page.seoIntentScore : 0;
          const salience = Number.isFinite(page?.salience) ? page.salience : undefined;
          const estimatedOrganicTraffic = typeof page?.estimatedOrganicTraffic === 'number'
            ? page.estimatedOrganicTraffic
            : undefined;
          const current = byUrl.get(pageUrl);
          if (!current || score > current.score) {
            byUrl.set(pageUrl, { url: pageUrl, score, salience, estimatedOrganicTraffic });
          }
        }

        return Array.from(byUrl.values())
          .sort((a, b) => b.score - a.score)
          .slice(0, 5);
      }),
      catchError(() => of([] as CompetitorTopicUrlPreview[]))
    ).subscribe((urls) => {
      this.competitorTopicTooltipCache[key] = {
        status: urls.length > 0 ? 'ready' : 'error',
        urls
      };
    });
  }

  onCompetitorPillLeave(): void {
    this.scheduleCompetitorTooltipHide();
  }

  onCompetitorPopoverEnter(match: CompetitorTopicMatch): void {
    this.cancelCompetitorTooltipHide();
    this.activeCompetitorTooltipKey = this.getCompetitorTopicTooltipKey(match);
  }

  onCompetitorPopoverLeave(): void {
    this.scheduleCompetitorTooltipHide();
  }

  private scheduleCompetitorTooltipHide(): void {
    this.cancelCompetitorTooltipHide();
    this.competitorTooltipHideTimer = setTimeout(() => {
      this.activeCompetitorTooltipKey = null;
      this.competitorTooltipHideTimer = null;
    }, 180);
  }

  private cancelCompetitorTooltipHide(): void {
    if (!this.competitorTooltipHideTimer) return;
    clearTimeout(this.competitorTooltipHideTimer);
    this.competitorTooltipHideTimer = null;
  }

  isCompetitorTooltipVisible(match: CompetitorTopicMatch): boolean {
    return this.activeCompetitorTooltipKey === this.getCompetitorTopicTooltipKey(match);
  }

  getCompetitorTooltipState(match: CompetitorTopicMatch): CompetitorTopicTooltipState {
    const key = this.getCompetitorTopicTooltipKey(match);
    return this.competitorTopicTooltipCache[key] || { status: 'idle', urls: [] };
  }

  getCompetitorPillTooltip(match: CompetitorTopicMatch): string {
    const key = this.getCompetitorTopicTooltipKey(match);
    const state = this.competitorTopicTooltipCache[key];
    const summary = `${match.domain}\nAuthority: ${Math.round(match.authorityScore)}\nURLs in topic: ${match.pageCount}`;

    if (!state || state.status === 'idle') {
      return `${summary}\n\nHover to load top URLs...`;
    }
    if (state.status === 'loading') {
      return `${summary}\n\nLoading URLs...`;
    }
    if (state.status === 'error') {
      return `${summary}\n\nNo URL-level preview available.`;
    }

    const lines = state.urls.map((item, i) => `${i + 1}. ${item.url} (${item.score.toFixed(3)})`);
    return `${summary}\n\nTop URLs:\n${lines.join('\n')}`;
  }

  onCompetitorPopoverUrlClick(match: CompetitorTopicMatch, competitorUrl: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.openQuickCompareWithUrl(competitorUrl || '');
  }

  onQuickCompareDrawerClose(): void {
    this.quickCompareOpen = false;
  }

  onOpenFullCompareFromDrawer(): void {
    const primary = (this.quickComparePrimaryUrl || '').trim();
    const compare = (this.quickCompareCompareUrl || '').trim();
    if (!primary || !compare) return;
    this.quickCompareOpen = false;
    void this.router.navigate(['/page-comparison'], {
      queryParams: {
        fullCompareYour: primary,
        fullCompareCompetitor: compare
      }
    });
  }

  private decodeUrlParam(raw: string): string {
    let value = (raw || '').trim();
    if (!value) return '';

    for (let i = 0; i < 3; i++) {
      try {
        const decoded = decodeURIComponent(value);
        if (decoded === value) break;
        value = decoded;
      } catch {
        break;
      }
    }

    return value;
  }

  private normalizeNavigableUrl(raw: string): string | null {
    const decoded = this.decodeUrlParam(raw);
    const trimmed = decoded.trim();
    if (!trimmed) return null;
    try {
      const u = new URL(trimmed);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      return u.href;
    } catch {
      return null;
    }
  }
} 